import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner, Stack } from 'react-bootstrap';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './ContractManager.css';

type ContractStatus = 'active' | 'cancelled' | 'superseded' | 'pending';
type ContractType = 'recurring' | 'one_off';
type SortField = 'customerName' | 'type' | 'startDate' | 'expiryDate' | 'hoursPerMonth' | 'totalContractValue';

interface ContractDocument {
    documentId?: number;
    contractId?: number;
    title: string;
    sharepointUrl: string;
    documentType?: string | null;
    notes?: string | null;
    markdownContent?: string | null;
}

interface ContractSummary {
    contractId: number;
    type: ContractType;
    customerName: string;
    documents?: ContractDocument[];
}

interface Contract {
    contractId: number;
    type: ContractType;
    linkedRecurringContractId: number | null;
    linkedRecurringContract?: ContractSummary | null;
    customerName: string;
    freeagentCustomerId: number;
    freeagentRecurringInvoiceId: number | null;
    freeagentProjectId: number | null;
    hoursPerMonth: number | null;
    notes: string | null;
    attachment: string | null;
    startDate: string | null;
    expiryDate: string | null;
    status: ContractStatus;
    willTerminateOnExpiry: boolean;
    remindSupplier: boolean;
    remindCustomer: boolean;
    supersededById: number | null;
    totalContractValue?: number | string | null;
    documents?: ContractDocument[];
    latestGeneration?: GeneratedContract | null;
    contractTemplateKey?: string | null;
    clientEntityType?: string | null;
    clientLegalName?: string | null;
    clientDisplayName?: string | null;
    clientRegisteredAddress?: string | null;
    clientCompanyNumber?: string | null;
    clientCharityNumber?: string | null;
    clientNoticeEmail?: string | null;
    clientSignerName?: string | null;
    clientSignerEmail?: string | null;
    clientSignerTitle?: string | null;
    siteUrl?: string | null;
    siteSoftwareAndSpecification?: string | null;
    siteSpecification?: string | null;
    hostingServicesAndCharges?: string | null;
    monthlyCharge?: number | string | null;
    includedSupportHours?: number | string | null;
    hourlyRate?: number | string | null;
    firstPaymentTerms?: string | null;
    changeControlDetails?: string | null;
    ccnValidityWorkingDays?: number | string | null;
}

interface FreeAgentProject { id: number; name: string; url: string; }
interface FreeAgentContact {
    id: number;
    firstName: string;
    lastName: string;
    organisationName: string;
    url: string;
    email?: string;
    billingEmail?: string;
    address1?: string;
    address2?: string;
    address3?: string;
    town?: string;
    region?: string;
    postcode?: string;
    country?: string;
}

interface ContractTemplateField {
    name: string;
    label: string;
    type: 'text' | 'textarea' | 'email' | 'url' | 'date' | 'number' | 'money' | 'select';
    required?: boolean;
    requiredWhen?: Record<string, string>;
    mustEqual?: string;
    computed?: boolean;
    defaultValue?: string;
    options?: Array<{ value: string; label: string }>;
}

interface ContractTemplateVersion {
    id: number;
    version: string;
    renewalEligible: boolean;
    placeholderSchema: ContractTemplateField[];
    qualityControlled?: boolean;
}

interface ContractTemplate {
    id: number;
    key: string;
    name: string;
    family: string | null;
    description: string | null;
    activeVersion: ContractTemplateVersion | null;
    versions?: ContractTemplateVersion[];
}

interface GeneratedContractDocument {
    id: number;
    type: string;
    filename: string;
    sharepointWebUrl: string | null;
    size: number | null;
}

interface GeneratedContract {
    id: number;
    contractId: number | null;
    status: string;
    referenceNumber: string;
    outputBasename: string;
    createdAt?: string | null;
    templateVersionId?: number | null;
    templateVersion?: {
        id: number;
        version: string;
    } | null;
    inputPayload?: Record<string, unknown> | null;
    supersedesGenerationId?: number | null;
    errorMessage?: string | null;
    template?: {
        key: string;
        name: string;
        family: string | null;
    } | null;
    documents: GeneratedContractDocument[];
    docusignEnvelope: {
        envelopeId: string;
        status: string | null;
        signerName: string | null;
        signerEmail: string | null;
        sentAt: string | null;
        completedAt: string | null;
    } | null;
}

interface NewContractData {
    type: ContractType;
    linkedRecurringContractId: number | '';
    customerName: string;
    freeagentCustomerId: number;
    freeagentRecurringInvoiceId: number | '';
    freeagentProjectId: number | '';
    hoursPerMonth: number | '';
    notes: string;
    attachment: string;
    expiryDate: Date | null;
    startDate: Date | null;
    status: ContractStatus;
    willTerminateOnExpiry: boolean;
    remindSupplier: boolean;
    remindCustomer: boolean;
    supersededById: number | null;
    totalContractValue: number;
    documents: ContractDocument[];
}

interface GenerationFormData {
    contractId: number | '';
    freeagentContactId: number;
    freeagentProjectId: number;
    templateFamily: string;
    templateKey: string;
    templateVersionId: number;
    createContract: boolean;
    payload: Record<string, string>;
    contract: {
        type: ContractType;
        linkedRecurringContractId: number | '';
        customerName: string;
        freeagentRecurringInvoiceId: number;
        hoursPerMonth: number;
        totalContractValue: number;
        notes: string;
        startDate: Date;
        expiryDate: Date;
    };
    signerName: string;
    signerEmail: string;
    emailSubject: string;
}

type GenerationContractMode = 'existing' | 'new';
type GenerationErrors = Record<string, string[]>;

const createInitialGenerationForm = (): GenerationFormData => ({
    contractId: '',
    freeagentContactId: 0,
    freeagentProjectId: 0,
    templateFamily: '',
    templateKey: '',
    templateVersionId: 0,
    createContract: true,
    payload: {},
    contract: {
        type: 'recurring',
        linkedRecurringContractId: '',
        customerName: '',
        freeagentRecurringInvoiceId: 0,
        hoursPerMonth: 0,
        totalContractValue: 0,
        notes: '',
        startDate: new Date(),
        expiryDate: moment().add(1, 'year').toDate(),
    },
    signerName: '',
    signerEmail: '',
    emailSubject: '',
});

const statusOptions: ContractStatus[] = ['active', 'pending', 'cancelled', 'superseded'];
const typeOptions: ContractType[] = ['recurring', 'one_off'];
const defaultStatusFilters: Record<ContractStatus, boolean> = { active: true, pending: true, cancelled: false, superseded: false };
const defaultTypeFilters: Record<ContractType, boolean> = { recurring: true, one_off: true };
const apiBase = process.env.REACT_APP_API_URL ?? '';
const authHeaders = { headers: { Authorization: `Basic ${btoa(`${process.env.REACT_APP_API_USERNAME}:${process.env.REACT_APP_API_PASSWORD}`)}` } };
const annualHostingTemplateKey = 'annual_hosting_sla';
const defaultFirstPaymentTerms = "It is understood by all parties that the first month's hosting charge will be billed upon execution of this agreement, and that payment of that invoice is required for this agreement to take effect.";
const defaultCcnValidityWorkingDays = '10';
const fullWidthGenerationFields = new Set([
    'SITE_SPECIFICATION',
    'HOSTING_SERVICES_AND_CHARGES',
    'CHANGE_CONTROL_DETAILS',
    'FIRST_PAYMENT_TERMS',
    'RELEASE_SCOPE',
    'WORK_REFERENCES',
    'ACCEPTANCE_EVIDENCE',
    'KNOWN_ISSUES',
    'EXCLUSIONS',
    'DEPLOYMENT_AUTHORISATION',
    'DELIVERY_TIMETABLE',
    'AGREED_SCOPE_DOCUMENTS',
    'SOW_SCOPE_AND_DELIVERABLES',
    'CLIENT_DEPENDENCIES',
    'ASSUMPTIONS',
    'ACCEPTANCE_CRITERIA',
    'ACCEPTANCE_PROCEDURE',
    'THIRD_PARTY_COSTS',
    'CHANGE_CONTROL_PROCEDURE',
    'DATA_PROCESSING_PARTICULARS',
    'SPECIAL_CATEGORY_DATA_GATE',
    'EXPRESS_VARIATIONS',
    'CHANGE_REQUEST_SCOPE',
    'COMMERCIAL_TERMS',
    'SERVICE_AND_TERM_IMPACT',
    'DATA_PROCESSING_IMPACT',
    'OTHER_VARIATIONS',
]);

const defaultHostingSpecification = (payload: Record<string, string>): string => {
    const siteUrl = payload.SITE_URL || 'the agreed production website';
    const identity = [
        payload.CLIENT_LEGAL_NAME,
        payload.CLIENT_DISPLAY_NAME,
        siteUrl,
    ].join(' ').toLowerCase();

    if (identity.includes('above base camp') || identity.includes('abovebasecamp.com') || identity.includes('abcwp.gem.li')) {
        return [
            'The Site is the Above Base Camp public website at https://abovebasecamp.com, implemented as a responsive WordPress website using the custom ABC WordPress theme and agreed content structure accepted under release ABCWP-2026-06-17.',
            'The Site includes the public Home, Activities, Beliefs, Events, Discover and Contact pages, responsive navigation/header/footer, editorial content and imagery, event and cost information, and information request/contact forms that submit through the WordPress form handler.',
            'The WordPress implementation includes editable content fields for the agreed page sections, repeatable event and pricing rows, integration points for EmailOctopus/GemMailer where configured, Google reCAPTCHA where configured, and standard WordPress administration access for authorised Customer content updates.',
            'The Specification excludes future direct e-commerce checkout, live booking/payment functionality, new pages, new integrations and material design or functionality changes unless agreed through change control.',
        ].join('\n\n');
    }

    return [
        `The Site is a responsive WordPress website at ${siteUrl}, using the agreed theme, plugins, content structure and forms accepted at the point of go-live or renewal.`,
        'The Specification includes standard WordPress administration access for authorised Customer content updates and the public-facing pages, forms and integrations already accepted for the Site.',
        'Material new functionality, new integrations, new pages, direct e-commerce checkout, booking/payment features and material design changes are outside the Specification unless agreed through change control.',
    ].join('\n\n');
};

export const addSchemaDefaults = (payload: Record<string, string>, version?: ContractTemplateVersion | null) => {
    const nextPayload = { ...payload };
    (version?.placeholderSchema ?? []).forEach(field => {
        if (Object.prototype.hasOwnProperty.call(payload, field.name)) return;

        if (field.name === 'CCN_VALIDITY_WORKING_DAYS') {
            nextPayload[field.name] = field.defaultValue ?? defaultCcnValidityWorkingDays;
        } else if (field.name === 'SITE_SPECIFICATION' && !version?.qualityControlled) {
            nextPayload[field.name] = field.defaultValue ?? defaultHostingSpecification(nextPayload);
        } else {
            nextPayload[field.name] = field.defaultValue
                ?? (field.type === 'date' && (!version?.qualityControlled || field.name === 'CONTRACT_DATE') ? moment().format('YYYY-MM-DD') : '');
        }
    });

    return nextPayload;
};

export const generationDateInputValue = (value: string): string => {
    if (!value) return '';

    const parsed = moment(value, [
        'YYYY-MM-DD',
        'D MMMM YYYY',
        'DD MMMM YYYY',
        'D MMM YYYY',
        'DD MMM YYYY',
    ], true);

    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const stringPayload = (payload?: Record<string, unknown> | null): Record<string, string> => Object.fromEntries(
    Object.entries(payload ?? {}).map(([key, value]) => [key, value === null || value === undefined ? '' : String(value)])
);

const payloadWithSigner = (form: GenerationFormData): Record<string, string> => ({
    ...form.payload,
    CLIENT_SIGNER_NAME: form.signerName || form.payload.CLIENT_SIGNER_NAME || '',
    CLIENT_SIGNER_EMAIL: form.signerEmail || form.payload.CLIENT_SIGNER_EMAIL || '',
});

export const generationPreviewFingerprint = (form: GenerationFormData): string => {
    const payload = payloadWithSigner(form);
    return JSON.stringify({
        templateKey: form.templateKey,
        templateVersionId: form.templateVersionId,
        createContract: form.createContract,
        contractId: form.contractId,
        freeagentContactId: form.freeagentContactId,
        freeagentProjectId: form.freeagentProjectId,
        contract: {
            ...form.contract,
            startDate: moment(form.contract.startDate).format('YYYY-MM-DD'),
            expiryDate: moment(form.contract.expiryDate).format('YYYY-MM-DD'),
        },
        payload: Object.keys(payload).sort().map(key => [key, payload[key]]),
    });
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

export const generationClientErrors = (
    form: GenerationFormData,
    version: ContractTemplateVersion | null,
    mode: GenerationContractMode,
    isRevision: boolean
): GenerationErrors => {
    const errors: GenerationErrors = {};
    const add = (key: string, message: string) => { errors[key] = [...(errors[key] ?? []), message]; };

    if (!form.templateFamily) add('templateFamily', 'Select an agreement type.');
    if (!form.templateKey) add('templateKey', 'Select an agreement template.');
    if (!form.templateVersionId) add('templateVersionId', 'Select a template version.');
    if (!isRevision && mode === 'existing' && !form.contractId) add('contractId', 'Select the existing contract this agreement belongs to.');
    if (!isRevision && mode === 'new') {
        if (!form.freeagentContactId) add('freeagentContactId', 'Select a FreeAgent client for the new contract.');
        if (!form.contract.customerName.trim()) add('contract.customerName', 'Enter a name for the new contract.');
        if (form.contract.type === 'one_off' && !form.freeagentProjectId) {
            add('freeagentProjectId', 'Select a FreeAgent project for the one-off contract.');
        }
    }

    (version?.placeholderSchema ?? []).filter(field => !field.computed).forEach(field => {
        const key = `payload.${field.name}`;
        const value = field.name === 'CLIENT_SIGNER_NAME'
            ? (form.signerName || form.payload.CLIENT_SIGNER_NAME || '')
            : field.name === 'CLIENT_SIGNER_EMAIL'
                ? (form.signerEmail || form.payload.CLIENT_SIGNER_EMAIL || '')
                : (form.payload[field.name] ?? '');

        const required = field.required || Object.entries(field.requiredWhen ?? {})
            .some(([dependency, expected]) => form.payload[dependency] === expected);
        const text = String(value).trim();
        if (required && (!text || /^\[.+\]$/.test(text) || text.includes('{{'))) add(key, `${field.label} is required.`);
        if (String(value).trim() && field.type === 'email' && !isValidEmail(String(value))) {
            add(key, `${field.label} must be a valid email address.`);
        }
        if (text && field.type === 'select' && !field.options?.some(option => option.value === text)) {
            add(key, `${field.label} must be a listed value.`);
        }
        if (field.mustEqual && text !== field.mustEqual) add(key, `${field.label} must be confirmed.`);
        if (text && (field.type === 'number' || field.type === 'money') && (!Number.isFinite(Number(text)) || Number(text) < 0)) {
            add(key, `${field.label} must be a non-negative number.`);
        }
    });

    if (version?.qualityControlled && form.payload.SERVICE_START_DATE && form.payload.SERVICE_END_DATE
        && moment(form.payload.SERVICE_END_DATE).isSameOrBefore(moment(form.payload.SERVICE_START_DATE), 'day')) {
        add('payload.SERVICE_END_DATE', 'Service end must be after service start.');
    }

    return errors;
};

const emptyDocument = (): ContractDocument => ({
    title: '',
    sharepointUrl: '',
    documentType: '',
    notes: '',
    markdownContent: '',
});

const formatContractType = (type: ContractType) => type === 'one_off' ? 'One-off' : 'Recurring';

const ContractManager: React.FC = () => {
    const initialEditFormData: Contract = {
        contractId: 0, type: 'recurring', linkedRecurringContractId: null, linkedRecurringContract: null,
        customerName: '', freeagentCustomerId: 0, freeagentRecurringInvoiceId: 0, freeagentProjectId: 0,
        hoursPerMonth: 0, notes: '', attachment: null, expiryDate: '', startDate: '', status: 'active',
        willTerminateOnExpiry: false, remindSupplier: false, remindCustomer: false, supersededById: null, totalContractValue: 0,
        documents: [],
    };
    const initialNewContractData: NewContractData = {
        type: 'recurring', linkedRecurringContractId: '',
        customerName: '', freeagentCustomerId: 0, freeagentRecurringInvoiceId: 0, freeagentProjectId: 0,
        hoursPerMonth: 0, notes: '', attachment: '', expiryDate: new Date(), startDate: new Date(), status: 'active',
        willTerminateOnExpiry: false, remindSupplier: true, remindCustomer: false, supersededById: null, totalContractValue: 0,
        documents: [],
    };

    const [showModal, setShowModal] = useState(false);
    const [newContractData, setNewContractData] = useState(initialNewContractData);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editContractId, setEditContractId] = useState<number | null>(null);
    const [editFormData, setEditFormData] = useState<Contract>(initialEditFormData);
    const [updatingContract, setUpdatingContract] = useState(false);
    const [freeAgentContacts, setFreeAgentContacts] = useState<FreeAgentContact[]>([]);
    const [projects, setProjects] = useState<FreeAgentProject[]>([]);
    const [projectsLoading, setProjectsLoading] = useState(false);
    const [projectsError, setProjectsError] = useState<string | null>(null);
    const [statusFilters, setStatusFilters] = useState(defaultStatusFilters);
    const [typeFilters, setTypeFilters] = useState(defaultTypeFilters);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState<SortField>('customerName');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [contractTemplates, setContractTemplates] = useState<ContractTemplate[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [showGenerationModal, setShowGenerationModal] = useState(false);
    const [generationForm, setGenerationForm] = useState<GenerationFormData>(createInitialGenerationForm);
    const [generationContractMode, setGenerationContractMode] = useState<GenerationContractMode>('existing');
    const [revisionSource, setRevisionSource] = useState<GeneratedContract | null>(null);
    const [loadingGenerationDetail, setLoadingGenerationDetail] = useState(false);
    const [generationDetailLoadFailed, setGenerationDetailLoadFailed] = useState(false);
    const [generationErrors, setGenerationErrors] = useState<GenerationErrors>({});
    const [previewFingerprint, setPreviewFingerprint] = useState<string | null>(null);
    const [generationHistories, setGenerationHistories] = useState<Record<number, GeneratedContract[]>>({});
    const [generationHistoryErrors, setGenerationHistoryErrors] = useState<Record<number, string>>({});
    const [generatingContract, setGeneratingContract] = useState(false);
    const [sendingDocusign, setSendingDocusign] = useState(false);
    const [generatedContract, setGeneratedContract] = useState<GeneratedContract | null>(null);
    const projectRequestController = useRef<AbortController | null>(null);
    const generationFormRef = useRef<HTMLFormElement | null>(null);

    const isActiveAndExpired = (contract: Contract) => contract.status === 'active' && Boolean(contract.expiryDate) && moment(contract.expiryDate).isBefore(moment());
    const formatDate = (date?: string | null) => (date ? moment(date).format('DD-MM-YYYY') : 'Not set');
    const parseDate = (date?: string | null) => (date ? moment(date).toDate() : null);
    const contactName = (contactId: number) => {
        const contact = freeAgentContacts.find(item => Number(item.url.split('/').pop()) === contactId);
        return contact ? `${contact.organisationName} ${moment().format('MMMM YYYY')}` : '';
    };
    const families = Array.from(new Set(contractTemplates.map(template => template.family || 'other'))).sort((a, b) => {
        if (a === 'hosting') return -1;
        if (b === 'hosting') return 1;
        return a.localeCompare(b);
    });
    const selectedFamilyTemplates = contractTemplates.filter(template => (template.family || 'other') === generationForm.templateFamily);
    const selectedGenerationTemplate = contractTemplates.find(template => template.key === generationForm.templateKey)
        ?? contractTemplates.find(template => template.activeVersion?.id === Number(generationForm.templateVersionId));
    const selectedGenerationVersion = (selectedGenerationTemplate?.versions ?? [])
        .find(version => version.id === Number(generationForm.templateVersionId))
        ?? selectedGenerationTemplate?.activeVersion
        ?? null;
    const selectedGenerationFields = (selectedGenerationVersion?.placeholderSchema ?? [])
        .filter(field => !field.computed && field.name !== 'CLIENT_SIGNER_NAME' && field.name !== 'CLIENT_SIGNER_EMAIL' && field.name !== 'CLIENT_SIGNER_TITLE');
    const clientGenerationErrors = generationClientErrors(
        generationForm,
        selectedGenerationVersion,
        generationContractMode,
        Boolean(revisionSource)
    );
    const allGenerationErrors = Object.entries({ ...clientGenerationErrors, ...generationErrors }).reduce<GenerationErrors>((result, [key, messages]) => {
        result[key] = Array.from(new Set([...(clientGenerationErrors[key] ?? []), ...(generationErrors[key] ?? []), ...messages]));
        return result;
    }, {});
    const hasBlockingGenerationErrors = Object.entries(allGenerationErrors)
        .some(([key, messages]) => key !== '_form' && messages.length > 0);
    const previewIsStale = Boolean(generatedContract && previewFingerprint && previewFingerprint !== generationPreviewFingerprint(generationForm));
    const revisionCandidateForContract = (contract: Contract) => contract.latestGeneration
        ?? generationHistories[contract.contractId]?.[0]
        ?? null;
    const generationIsReadyToSend = (generation: GeneratedContract | null | undefined) => Boolean(
        generation
        && generation.status === 'generated'
        && !generation.docusignEnvelope
    );

    const normalizeAttachment = (value?: string | null) => {
        const trimmed = value?.trim() ?? '';
        return trimmed.length ? trimmed : null;
    };
    const normalizeDocuments = (documents: ContractDocument[]) => documents
        .map(document => ({
            ...document,
            title: document.title.trim(),
            sharepointUrl: document.sharepointUrl.trim(),
            documentType: document.documentType?.trim() || null,
            notes: document.notes?.trim() || null,
            markdownContent: document.markdownContent?.trim() || null,
        }))
        .filter(document => document.title || document.sharepointUrl || document.documentType || document.notes || document.markdownContent);
    const firstApiError = (requestError: any, fallback: string) => {
        const validationErrors = requestError.response?.data?.errors;
        if (validationErrors) {
            const firstKey = Object.keys(validationErrors)[0];
            const firstValue = firstKey ? validationErrors[firstKey] : null;
            if (Array.isArray(firstValue) && firstValue.length > 0) {
                return String(firstValue[0]);
            }
        }

        return String(requestError.response?.data?.message || fallback);
    };

    const apiGenerationErrors = (requestError: any, fallback: string): GenerationErrors => {
        const validationErrors = requestError.response?.data?.errors;
        if (validationErrors && typeof validationErrors === 'object') {
            return Object.fromEntries(Object.entries(validationErrors).map(([key, value]) => [
                key,
                Array.isArray(value) ? value.map(String) : [String(value)],
            ]));
        }

        return { _form: [String(requestError.response?.data?.message || fallback)] };
    };

    const focusFirstGenerationError = (errors: GenerationErrors) => {
        const firstKey = Object.keys(errors).find(key => errors[key]?.length && key !== '_form');
        if (!firstKey) return;
        window.setTimeout(() => {
            const control = generationFormRef.current?.querySelector<HTMLElement>(`[data-generation-field="${firstKey}"]`);
            control?.focus();
        }, 0);
    };

    const setModalGenerationErrors = (errors: GenerationErrors) => {
        setGenerationErrors(errors);
        focusFirstGenerationError(errors);
    };

    const contactDisplayName = (contact: FreeAgentContact) => contact.organisationName || `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
    const contactIdFromUrl = (contact: FreeAgentContact) => Number(contact.url.split('/').pop() ?? contact.id) || contact.id;
    const contactAddress = (contact: FreeAgentContact) => [
        contact.address1,
        contact.address2,
        contact.address3,
        contact.town,
        contact.region,
        contact.postcode,
        contact.country,
    ].filter(Boolean).join(', ');

    const initials = (name: string) => name
        .split(/[^A-Za-z0-9]+/)
        .filter(word => word && !['THE', 'AND', 'OF', 'LTD', 'LIMITED', 'PLC', 'CIC', 'LLP'].includes(word.toUpperCase()))
        .map(word => word[0].toUpperCase())
        .join('')
        .slice(0, 4) || 'GCM';

    const inferEntityType = (name: string): string => {
        if (/\b(t\/a|trading\s+as)\b/i.test(name)) return 'sole_trader';
        if (/\b(ltd|limited|plc|llp|cic)\b/i.test(name)) return 'company';
        if (/\b(charity|foundation|church|parish|trust|pre-school|preschool)\b/i.test(name)) return 'charity';
        return 'company';
    };

    const formatFamilyLabel = (family: string) => {
        const labels: Record<string, string> = {
            hosting: 'Hosting / SLA',
            development: 'Development / one-off',
            support: 'Support / retainer',
            acceptance: 'Acceptance',
            consultancy: 'Consulting',
            sow: 'Statement of work',
            variation: 'Variation / change control',
            other: 'Other',
        };

        return labels[family] ?? family.replace(/[_-]+/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
    };

    const templateVersions = (template?: ContractTemplate | null) => template?.versions?.length
        ? template.versions
        : (template?.activeVersion ? [template.activeVersion] : []);

    const activeVersionId = (template?: ContractTemplate | null) => template?.activeVersion?.id
        ?? templateVersions(template)[0]?.id
        ?? 0;

    const defaultTemplateForContract = (contract?: Contract) => {
        if (contract?.contractTemplateKey) {
            const savedTemplate = contractTemplates.find(template => template.key === contract.contractTemplateKey);
            if (savedTemplate) return savedTemplate;
        }

        if (!contract || contract.type === 'recurring') {
            return contractTemplates.find(template => template.key === annualHostingTemplateKey)
                ?? contractTemplates.find(template => template.family === 'hosting')
                ?? contractTemplates[0];
        }

        if (contract.linkedRecurringContractId) {
            return contractTemplates.find(template => template.key === 'statement_of_work')
                ?? contractTemplates.find(template => template.family === 'sow')
                ?? contractTemplates.find(template => template.family === 'development')
                ?? contractTemplates[0];
        }

        return contractTemplates.find(template => template.family === 'development')
            ?? contractTemplates.find(template => template.family === 'hosting')
            ?? contractTemplates[0];
    };

    const defaultTemplateForNewContract = (type: ContractType, linkedRecurringContractId: number | '') => {
        if (type === 'recurring') {
            return contractTemplates.find(template => template.key === annualHostingTemplateKey)
                ?? contractTemplates.find(template => template.family === 'hosting')
                ?? contractTemplates[0];
        }

        if (linkedRecurringContractId) {
            return contractTemplates.find(template => template.key === 'statement_of_work')
                ?? contractTemplates.find(template => template.family === 'sow')
                ?? contractTemplates.find(template => template.family === 'development')
                ?? contractTemplates[0];
        }

        return contractTemplates.find(template => template.family === 'development')
            ?? contractTemplates.find(template => template.family === 'sow')
            ?? contractTemplates[0];
    };

    const seedPayloadFromContact = (contact?: FreeAgentContact | null): Record<string, string> => {
        if (!contact) return {};

        const displayName = contactDisplayName(contact);
        const noticeEmail = contact.billingEmail || contact.email || '';

        return {
            CLIENT_ENTITY_TYPE: inferEntityType(displayName),
            CLIENT_LEGAL_NAME: displayName,
            CLIENT_DISPLAY_NAME: displayName,
            CLIENT_SHORT_NAME: displayName || initials(displayName),
            CLIENT_REGISTERED_ADDRESS: contactAddress(contact),
            CLIENT_NOTICE_EMAIL: noticeEmail,
            CLIENT_SIGNER_NAME: displayName,
            CLIENT_SIGNER_EMAIL: noticeEmail,
            CONTRACT_DATE: moment().format('YYYY-MM-DD'),
        };
    };

    const seedPayloadFromContract = (contract: Contract): Record<string, string> => {
        const baseName = contract.clientLegalName || contract.clientDisplayName || contract.customerName || '';
        const payload: Record<string, string> = {
            CLIENT_ENTITY_TYPE: contract.clientEntityType || inferEntityType(baseName),
            CLIENT_LEGAL_NAME: baseName,
            CLIENT_DISPLAY_NAME: contract.clientDisplayName || baseName,
            CLIENT_SHORT_NAME: contract.clientDisplayName || baseName,
            CLIENT_REGISTERED_ADDRESS: contract.clientRegisteredAddress || '',
            CLIENT_COMPANY_NUMBER: contract.clientCompanyNumber || '',
            CLIENT_CHARITY_NUMBER: contract.clientCharityNumber || '',
            CLIENT_NOTICE_EMAIL: contract.clientNoticeEmail || '',
            CLIENT_SIGNER_NAME: contract.clientSignerName || '',
            CLIENT_SIGNER_EMAIL: contract.clientSignerEmail || '',
            CLIENT_SIGNER_TITLE: contract.clientSignerTitle || '',
            SITE_URL: contract.siteUrl || '',
            SITE_SOFTWARE_AND_SPECIFICATION: contract.siteSoftwareAndSpecification || '',
            SITE_SPECIFICATION: contract.siteSpecification || '',
            HOSTING_SERVICES_AND_CHARGES: contract.hostingServicesAndCharges || '',
            MONTHLY_CHARGE: contract.monthlyCharge !== null && contract.monthlyCharge !== undefined ? String(contract.monthlyCharge) : '',
            INCLUDED_SUPPORT_HOURS: contract.includedSupportHours !== null && contract.includedSupportHours !== undefined
                ? String(contract.includedSupportHours)
                : (contract.hoursPerMonth !== null && contract.hoursPerMonth !== undefined ? String(contract.hoursPerMonth) : ''),
            HOURLY_RATE: contract.hourlyRate !== null && contract.hourlyRate !== undefined ? String(contract.hourlyRate) : '',
            FIRST_PAYMENT_TERMS: contract.firstPaymentTerms || defaultFirstPaymentTerms,
            CHANGE_CONTROL_DETAILS: contract.changeControlDetails || '',
            CCN_VALIDITY_WORKING_DAYS: contract.ccnValidityWorkingDays !== null && contract.ccnValidityWorkingDays !== undefined
                ? String(contract.ccnValidityWorkingDays)
                : defaultCcnValidityWorkingDays,
            CONTRACT_DATE: moment().format('YYYY-MM-DD'),
        };

        return Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== null && value !== undefined)) as Record<string, string>;
    };

    const selectedContact = (contactId: number) => freeAgentContacts.find(item => contactIdFromUrl(item) === contactId) ?? null;

    const fetchGenerationHistories = useCallback(async (contractsToLoad: Contract[]) => {
        if (contractsToLoad.length === 0) {
            setGenerationHistories({});
            return;
        }

        const results = await Promise.all(contractsToLoad.map(async contract => {
            try {
                const response = await axios.get(`${apiBase}/contracts/${contract.contractId}/generations`, authHeaders);
                return { contractId: contract.contractId, generations: response.data as GeneratedContract[], error: '' };
            } catch (requestError) {
                console.error(`Failed to load generation history for contract ${contract.contractId}`, requestError);
                return { contractId: contract.contractId, generations: null, error: 'Agreement history could not be loaded.' };
            }
        }));

        setGenerationHistories(previous => {
            const next = { ...previous };
            results.forEach(result => {
                if (result.generations) next[result.contractId] = result.generations;
            });
            return next;
        });
        setGenerationHistoryErrors(previous => {
            const next = { ...previous };
            results.forEach(result => {
                if (result.error) next[result.contractId] = result.error;
                else delete next[result.contractId];
            });
            return next;
        });
    }, []);

    const fetchContracts = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${apiBase}/contracts`, authHeaders);
            const loadedContracts = response.data as Contract[];
            setContracts(loadedContracts);
            await fetchGenerationHistories(loadedContracts);
        } catch (requestError) {
            console.error('Failed to load contracts', requestError);
            setError('Failed to load contracts');
        } finally {
            setLoading(false);
        }
    }, [fetchGenerationHistories]);

    const fetchFreeAgentContacts = useCallback(async () => {
        try {
            const response = await axios.get(`${apiBase}/freeagent-contacts`, authHeaders);
            setFreeAgentContacts(response.data);
        } catch (requestError) {
            console.error('Failed to load FreeAgent contacts', requestError);
            setError('Failed to load FreeAgent contacts');
        }
    }, []);

    const fetchContractTemplates = useCallback(async () => {
        setTemplatesLoading(true);
        try {
            const response = await axios.get(`${apiBase}/contract-templates`, authHeaders);
            setContractTemplates(response.data);
        } catch (requestError) {
            console.error('Failed to load contract templates', requestError);
            setError('Failed to load contract templates');
        } finally {
            setTemplatesLoading(false);
        }
    }, []);

    const fetchProjectsByContact = async (contactId: number) => {
        if (projectRequestController.current) projectRequestController.current.abort();
        if (!contactId) { setProjects([]); return; }
        const controller = new AbortController();
        projectRequestController.current = controller;
        setProjects([]); setProjectsLoading(true); setProjectsError(null);
        try {
            const response = await axios.get(`${apiBase}/freeagent-contacts/${contactId}/projects`, { ...authHeaders, signal: controller.signal });
            setProjects(response.data);
        } catch (requestError: any) {
            if (requestError.code !== 'ERR_CANCELED') {
                console.error(`Failed to load projects for contact ${contactId}`, requestError);
                setProjectsError(`Failed to load projects for contact ${contactId}`);
            }
        } finally {
            setProjectsLoading(false);
        }
    };

    useEffect(() => {
        fetchContracts();
        fetchFreeAgentContacts();
        fetchContractTemplates();
    }, [fetchContracts, fetchFreeAgentContacts, fetchContractTemplates]);

    useEffect(() => {
        if (!showGenerationModal || loadingGenerationDetail) return;
        window.setTimeout(() => {
            generationFormRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
        }, 0);
    }, [showGenerationModal, loadingGenerationDetail, generationForm.templateVersionId, generationContractMode]);

    const recurringContracts = contracts.filter(contract => contract.type === 'recurring');
    const normalizedSearchTerm = searchTerm.trim().toLowerCase();
    const filteredAndSortedContracts = [...contracts]
        .filter(contract => statusFilters[contract.status])
        .filter(contract => typeFilters[contract.type ?? 'recurring'])
        .filter(contract => {
            if (!normalizedSearchTerm) return true;

            const haystack = [
                contract.customerName,
                contract.type,
                contract.status,
                contract.notes,
                contract.freeagentCustomerId,
                contract.freeagentProjectId,
                contract.freeagentRecurringInvoiceId,
                contract.linkedRecurringContract?.customerName,
                ...(contract.documents ?? []).flatMap(document => [
                    document.title,
                    document.sharepointUrl,
                    document.documentType,
                    document.notes,
                    document.markdownContent,
                ]),
            ].filter(Boolean).join(' ').toLowerCase();

            return haystack.includes(normalizedSearchTerm);
        })
        .sort((a, b) => {
            const dir = sortDirection === 'asc' ? 1 : -1;
            switch (sortField) {
                case 'customerName': return dir * a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' });
                case 'type': return dir * (a.type ?? 'recurring').localeCompare(b.type ?? 'recurring');
                case 'startDate': return dir * (moment(a.startDate ?? '').valueOf() - moment(b.startDate ?? '').valueOf());
                case 'expiryDate': return dir * (moment(a.expiryDate ?? '').valueOf() - moment(b.expiryDate ?? '').valueOf());
                case 'hoursPerMonth': return dir * (Number(a.hoursPerMonth ?? 0) - Number(b.hoursPerMonth ?? 0));
                case 'totalContractValue': return dir * (Number(a.totalContractValue ?? 0) - Number(b.totalContractValue ?? 0));
                default: return 0;
            }
        });

    const handleOpenModal = () => setShowModal(true);
    const handleCloseModal = () => { setShowModal(false); setNewContractData(initialNewContractData); };
    const createGenerationFormForContract = (contract?: Contract): GenerationFormData => {
        const initial = createInitialGenerationForm();
        const template = defaultTemplateForContract(contract);
        const versionId = activeVersionId(template);
        const version = templateVersions(template).find(item => item.id === versionId) ?? template?.activeVersion ?? null;

        if (template) {
            initial.templateFamily = template.family || 'other';
            initial.templateKey = template.key;
            initial.templateVersionId = versionId;
        }

        if (contract) {
            initial.contractId = contract.contractId;
            initial.freeagentContactId = contract.freeagentCustomerId;
            initial.freeagentProjectId = contract.freeagentProjectId ?? 0;
            initial.createContract = false;
            initial.contract.type = contract.type;
            initial.contract.linkedRecurringContractId = contract.linkedRecurringContractId ?? '';
            initial.contract.customerName = contract.customerName;
            initial.contract.freeagentRecurringInvoiceId = contract.freeagentRecurringInvoiceId ?? 0;
            initial.contract.hoursPerMonth = Number(contract.hoursPerMonth) || 0;
            initial.contract.totalContractValue = Number(contract.totalContractValue) || 0;
            initial.contract.notes = contract.notes ?? '';
            initial.contract.startDate = parseDate(contract.startDate) ?? new Date();
            initial.contract.expiryDate = parseDate(contract.expiryDate) ?? moment().add(1, 'year').toDate();
            if (contract.freeagentCustomerId) fetchProjectsByContact(contract.freeagentCustomerId);
            initial.payload = {
                ...seedPayloadFromContact(selectedContact(contract.freeagentCustomerId)),
                ...seedPayloadFromContract(contract),
            };
            initial.signerName = initial.payload.CLIENT_SIGNER_NAME || initial.payload.CLIENT_DISPLAY_NAME || initial.payload.CLIENT_LEGAL_NAME || '';
            initial.signerEmail = initial.payload.CLIENT_SIGNER_EMAIL || initial.payload.CLIENT_NOTICE_EMAIL || '';
        } else if (template) {
            initial.payload = {
                FIRST_PAYMENT_TERMS: defaultFirstPaymentTerms,
                CONTRACT_DATE: moment().format('YYYY-MM-DD'),
            };
        }

        initial.payload = addSchemaDefaults(initial.payload, version);
        initial.emailSubject = template ? `Contract for signature: ${template.name}` : '';
        return initial;
    };

    const createGenerationFormFromDetail = (contract: Contract, detail: GeneratedContract): GenerationFormData => {
        const form = createGenerationFormForContract(contract);
        const sourceVersionId = detail.templateVersion?.id ?? detail.templateVersionId ?? 0;
        const template = contractTemplates.find(item => item.key === detail.template?.key)
            ?? contractTemplates.find(item => templateVersions(item).some(version => version.id === sourceVersionId));
        const versionId = generationIsReadyToSend(detail) ? sourceVersionId : (template?.activeVersion?.id ?? sourceVersionId);
        const version = templateVersions(template).find(item => item.id === versionId)
            ?? (template?.activeVersion?.id === versionId ? template.activeVersion : null);
        const payload = addSchemaDefaults(stringPayload(detail.inputPayload), version);
        if (!generationIsReadyToSend(detail)) {
            delete payload._QUALITY_PROOF;
            if (version?.qualityControlled) payload.KNOWN_ISSUES_REVIEW_STATUS = '';
        }

        return {
            ...form,
            contractId: detail.contractId ?? contract.contractId,
            createContract: false,
            templateFamily: template?.family || detail.template?.family || form.templateFamily || 'other',
            templateKey: template?.key || detail.template?.key || form.templateKey,
            templateVersionId: versionId,
            payload,
            signerName: payload.CLIENT_SIGNER_NAME || detail.docusignEnvelope?.signerName || '',
            signerEmail: payload.CLIENT_SIGNER_EMAIL || detail.docusignEnvelope?.signerEmail || '',
            emailSubject: detail.template?.name
                ? `Contract for signature: ${detail.template.name}`
                : form.emailSubject,
        };
    };

    const resetGenerationModalState = () => {
        setGeneratedContract(null);
        setPreviewFingerprint(null);
        setGenerationErrors({});
        setGenerationDetailLoadFailed(false);
    };

    const handleOpenGenerationModal = (contract?: Contract) => {
        const initial = createGenerationFormForContract(contract);
        const mode: GenerationContractMode = contract || contracts.length > 0 ? 'existing' : 'new';
        initial.createContract = mode === 'new';
        setGenerationContractMode(mode);
        setRevisionSource(null);
        setLoadingGenerationDetail(false);
        setGenerationForm(initial);
        resetGenerationModalState();
        setShowGenerationModal(true);
    };

    const handleOpenRevision = async (contract: Contract, source: GeneratedContract) => {
        const initial = createGenerationFormForContract(contract);
        setGenerationContractMode('existing');
        setRevisionSource(source);
        setGenerationForm(initial);
        resetGenerationModalState();
        setLoadingGenerationDetail(true);
        setGenerationDetailLoadFailed(false);
        setShowGenerationModal(true);

        try {
            const response = await axios.get(`${apiBase}/contract-generations/${source.id}`, authHeaders);
            const detail = response.data as GeneratedContract;
            const hydratedForm = createGenerationFormFromDetail(contract, detail);
            setRevisionSource(detail);
            setGenerationForm(hydratedForm);
            if (generationIsReadyToSend(detail)) {
                setGeneratedContract(detail);
                setPreviewFingerprint(generationPreviewFingerprint(hydratedForm));
            }
        } catch (requestError: any) {
            console.error(`Failed to load generation ${source.id}`, requestError);
            setGenerationDetailLoadFailed(true);
            setModalGenerationErrors(apiGenerationErrors(requestError, 'Failed to load the selected agreement.'));
        } finally {
            setLoadingGenerationDetail(false);
        }
    };

    const handleCloseGenerationModal = () => {
        setShowGenerationModal(false);
        setGenerationForm(createInitialGenerationForm());
        setRevisionSource(null);
        setLoadingGenerationDetail(false);
        setGenerationDetailLoadFailed(false);
        resetGenerationModalState();
    };

    const handleNewContractChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const target = event.target as HTMLInputElement;
        if (target.type === 'checkbox') {
            setNewContractData(prev => ({ ...prev, [target.name]: target.checked } as NewContractData));
            return;
        }
        if (target.name === 'type') {
            const type = target.value as ContractType;
            setNewContractData(prev => ({
                ...prev,
                type,
                linkedRecurringContractId: type === 'one_off' ? prev.linkedRecurringContractId : '',
                freeagentRecurringInvoiceId: type === 'recurring' ? prev.freeagentRecurringInvoiceId : '',
                hoursPerMonth: type === 'recurring' ? prev.hoursPerMonth : '',
            }));
            return;
        }
        if (['freeagentRecurringInvoiceId', 'hoursPerMonth', 'totalContractValue', 'linkedRecurringContractId'].includes(target.name)) {
            setNewContractData(prev => ({ ...prev, [target.name]: target.value === '' ? '' : Number(target.value) } as NewContractData));
            return;
        }
        setNewContractData(prev => ({ ...prev, [target.name]: target.value } as NewContractData));
    };

    const handleNewContractDateChange = (field: 'expiryDate' | 'startDate', date: Date | null) => {
        setNewContractData(prev => ({ ...prev, [field]: date }));
    };

    const handleContactChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const contactId = Number((event.target as HTMLSelectElement).value) || 0;
        fetchProjectsByContact(contactId);
        if (editContractId !== null) {
            setEditFormData(prev => ({ ...prev, freeagentCustomerId: contactId, freeagentProjectId: 0 }));
            return;
        }
        setNewContractData(prev => ({ ...prev, freeagentCustomerId: contactId, freeagentProjectId: 0, customerName: contactName(contactId) }));
    };

    const handleGenerationContactChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const contactId = Number(event.target.value) || 0;
        const contact = freeAgentContacts.find(item => contactIdFromUrl(item) === contactId);
        fetchProjectsByContact(contactId);
        setGenerationErrors({});

        setGenerationForm(prev => {
            const contactPayload = seedPayloadFromContact(contact);
            const displayName = contactPayload.CLIENT_DISPLAY_NAME || '';
            const noticeEmail = contactPayload.CLIENT_NOTICE_EMAIL || '';
            const linkedRecurringContract = recurringContracts.find(item => item.contractId === Number(prev.contract.linkedRecurringContractId));
            const linkedRecurringContractId = linkedRecurringContract?.freeagentCustomerId === contactId
                ? prev.contract.linkedRecurringContractId
                : '';
            return {
                ...prev,
                freeagentContactId: contactId,
                freeagentProjectId: 0,
                contract: {
                    ...prev.contract,
                    linkedRecurringContractId,
                    customerName: displayName,
                },
                signerName: prev.signerName || displayName,
                signerEmail: prev.signerEmail || noticeEmail,
                payload: {
                    ...prev.payload,
                    ...contactPayload,
                    CLIENT_SIGNER_NAME: prev.payload.CLIENT_SIGNER_NAME || contactPayload.CLIENT_SIGNER_NAME || '',
                    CLIENT_SIGNER_EMAIL: prev.payload.CLIENT_SIGNER_EMAIL || contactPayload.CLIENT_SIGNER_EMAIL || '',
                    CONTRACT_DATE: prev.payload.CONTRACT_DATE || contactPayload.CONTRACT_DATE || moment().format('YYYY-MM-DD'),
                },
            };
        });
    };

    const updateGenerationTemplateSelection = (
        template: ContractTemplate | undefined,
        templateVersionId: number,
        previous: GenerationFormData
    ): GenerationFormData => {
        const version = templateVersions(template).find(item => item.id === templateVersionId) ?? template?.activeVersion ?? null;
        const payload = addSchemaDefaults(previous.payload, version);
        if (version?.qualityControlled) payload.KNOWN_ISSUES_REVIEW_STATUS = '';

        return {
            ...previous,
            templateFamily: template?.family || previous.templateFamily || 'other',
            templateKey: template?.key || '',
            templateVersionId: version?.id ?? 0,
            payload,
            emailSubject: template ? `Contract for signature: ${template.name}` : previous.emailSubject,
        };
    };

    const handleGenerationFamilyChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const family = event.target.value || 'other';
        const familyTemplates = contractTemplates.filter(template => (template.family || 'other') === family);
        const template = family === 'hosting'
            ? (familyTemplates.find(item => item.key === annualHostingTemplateKey) ?? familyTemplates[0])
            : familyTemplates[0];

        setGenerationErrors({});
        setGenerationForm(prev => updateGenerationTemplateSelection(template, activeVersionId(template), {
            ...prev,
            templateFamily: family,
        }));
    };

    const handleGenerationTemplateChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const templateKey = event.target.value;
        const template = contractTemplates.find(item => item.key === templateKey);

        setGenerationErrors({});
        setGenerationForm(prev => updateGenerationTemplateSelection(template, activeVersionId(template), prev));
    };

    const handleGenerationVersionChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const templateVersionId = Number(event.target.value) || 0;
        const template = selectedGenerationTemplate;

        setGenerationErrors({});
        setGenerationForm(prev => {
            return updateGenerationTemplateSelection(template, templateVersionId, prev);
        });
    };

    const handleGenerationPayloadChange = (field: ContractTemplateField, value: string) => {
        setGenerationErrors({});
        setGenerationForm(prev => {
            const nextPayload = {
                ...prev.payload,
                [field.name]: value,
            };
            if (selectedGenerationVersion?.qualityControlled && field.name !== 'KNOWN_ISSUES_REVIEW_STATUS') {
                nextPayload.KNOWN_ISSUES_REVIEW_STATUS = '';
            }

            const nextForm: GenerationFormData = {
                ...prev,
                payload: nextPayload,
            };

            if (field.name === 'CLIENT_LEGAL_NAME' || field.name === 'CLIENT_DISPLAY_NAME') {
                const displayName = nextPayload.CLIENT_DISPLAY_NAME || nextPayload.CLIENT_LEGAL_NAME || '';
                nextForm.contract = {
                    ...nextForm.contract,
                    customerName: nextForm.contract.customerName || displayName,
                };
            }

            if (field.name === 'CLIENT_SIGNER_NAME') nextForm.signerName = value;
            if (field.name === 'CLIENT_SIGNER_EMAIL') nextForm.signerEmail = value;

            return nextForm;
        });
    };

    const handleGenerationContractChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        const numericFields = ['freeagentRecurringInvoiceId', 'hoursPerMonth', 'totalContractValue'];
        setGenerationErrors({});
        setGenerationForm(prev => ({
            ...prev,
            contract: {
                ...prev.contract,
                [name]: numericFields.includes(name) ? Number(value) || 0 : value,
            },
        }));
    };

    const handleGenerationContractTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const type = event.target.value as ContractType;
        const linkedRecurringContractId = type === 'one_off' ? generationForm.contract.linkedRecurringContractId : '';
        const template = defaultTemplateForNewContract(type, linkedRecurringContractId);
        setGenerationErrors({});
        setGenerationForm(previous => updateGenerationTemplateSelection(template, activeVersionId(template), {
            ...previous,
            contract: {
                ...previous.contract,
                type,
                linkedRecurringContractId,
                freeagentRecurringInvoiceId: type === 'recurring' ? previous.contract.freeagentRecurringInvoiceId : 0,
                hoursPerMonth: type === 'recurring' ? previous.contract.hoursPerMonth : 0,
            },
        }));
    };

    const handleGenerationLinkedContractChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const linkedRecurringContractId = event.target.value ? Number(event.target.value) : '';
        const template = defaultTemplateForNewContract('one_off', linkedRecurringContractId);
        setGenerationErrors({});
        setGenerationForm(previous => updateGenerationTemplateSelection(template, activeVersionId(template), {
            ...previous,
            contract: {
                ...previous.contract,
                type: 'one_off',
                linkedRecurringContractId,
            },
        }));
    };

    const handleGenerationExistingContractChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const contractId = event.target.value ? Number(event.target.value) : 0;
        const contract = contracts.find(item => item.contractId === contractId);
        setGenerationErrors({});

        if (!contract) {
            setGenerationForm(prev => ({
                ...prev,
                contractId: '',
                createContract: false,
            }));
            return;
        }

        if (revisionCandidateForContract(contract)) {
            setGenerationForm(previous => ({ ...previous, contractId: '', createContract: false }));
            setModalGenerationErrors({
                _form: [`${contract.customerName} already has agreement history. Use Revise Agreement on its contract card or in its Agreement history.`],
            });
            return;
        }

        const template = defaultTemplateForContract(contract);
        const versionId = activeVersionId(template);
        const version = templateVersions(template).find(item => item.id === versionId) ?? template?.activeVersion ?? null;
        if (contract.freeagentCustomerId) fetchProjectsByContact(contract.freeagentCustomerId);

        setGenerationForm(prev => {
            const payload = addSchemaDefaults({
                ...seedPayloadFromContact(selectedContact(contract.freeagentCustomerId)),
                ...seedPayloadFromContract(contract),
            }, version);

            return {
                ...prev,
                contractId: contract.contractId,
                createContract: false,
                freeagentContactId: contract.freeagentCustomerId,
                freeagentProjectId: contract.freeagentProjectId ?? 0,
                templateFamily: template?.family || prev.templateFamily,
                templateKey: template?.key || prev.templateKey,
                templateVersionId: version?.id ?? prev.templateVersionId,
                payload,
                contract: {
                    ...prev.contract,
                    type: contract.type,
                    linkedRecurringContractId: contract.linkedRecurringContractId ?? '',
                    customerName: contract.customerName,
                    freeagentRecurringInvoiceId: contract.freeagentRecurringInvoiceId ?? 0,
                    hoursPerMonth: Number(contract.hoursPerMonth) || 0,
                    totalContractValue: Number(contract.totalContractValue) || 0,
                    notes: contract.notes ?? '',
                    startDate: parseDate(contract.startDate) ?? new Date(),
                    expiryDate: parseDate(contract.expiryDate) ?? moment().add(1, 'year').toDate(),
                },
                signerName: payload.CLIENT_SIGNER_NAME || payload.CLIENT_DISPLAY_NAME || payload.CLIENT_LEGAL_NAME || '',
                signerEmail: payload.CLIENT_SIGNER_EMAIL || payload.CLIENT_NOTICE_EMAIL || '',
                emailSubject: template ? `Contract for signature: ${template.name}` : prev.emailSubject,
            };
        });
    };

    const handleGenerationContractModeChange = (mode: GenerationContractMode) => {
        if (revisionSource) return;
        setGenerationContractMode(mode);
        setGenerationErrors({});
        setGenerationForm(previous => ({
            ...previous,
            contractId: mode === 'new' ? '' : previous.contractId,
            createContract: mode === 'new',
        }));
    };

    const handleGenerateContract = async () => {
        if (generationDetailLoadFailed) {
            setModalGenerationErrors({ _form: ['The saved agreement inputs were not loaded. Close this window and reopen the agreement before generating a revision.'] });
            return;
        }

        const validationErrors = generationClientErrors(
            generationForm,
            selectedGenerationVersion,
            generationContractMode,
            Boolean(revisionSource)
        );
        if (Object.keys(validationErrors).length > 0) {
            setModalGenerationErrors(validationErrors);
            return;
        }

        setGeneratingContract(true);
        setGenerationErrors({});
        try {
            const payload = payloadWithSigner(generationForm);
            const response = revisionSource
                ? await axios.post(`${apiBase}/contract-generations/${revisionSource.id}/revisions`, {
                    templateVersionId: generationForm.templateVersionId,
                    payload,
                }, authHeaders)
                : await axios.post(`${apiBase}/contract-generations`, {
                    templateVersionId: generationForm.templateVersionId,
                    contractId: generationContractMode === 'existing' ? generationForm.contractId || null : null,
                    freeagentContactId: generationForm.freeagentContactId || null,
                    freeagentProjectId: generationForm.freeagentProjectId || null,
                    createContract: generationContractMode === 'new',
                    requirePdf: true,
                    generationType: 'contract',
                    payload,
                    contract: {
                        ...generationForm.contract,
                        startDate: moment(generationForm.contract.startDate).format('YYYY-MM-DD'),
                        expiryDate: moment(generationForm.contract.expiryDate).format('YYYY-MM-DD'),
                        status: 'pending',
                        remindSupplier: true,
                        remindCustomer: false,
                        willTerminateOnExpiry: false,
                    },
                }, authHeaders);
            const nextGeneration = response.data as GeneratedContract;
            const contractId = nextGeneration.contractId ?? (Number(generationForm.contractId) || null);
            if (contractId) {
                setGenerationHistories(previous => ({
                    ...previous,
                    [contractId]: [
                        nextGeneration,
                        ...(previous[contractId] ?? []).filter(generation => generation.id !== nextGeneration.id),
                    ],
                }));
            }
            if (nextGeneration.status === 'failed') {
                setGeneratedContract(null);
                setPreviewFingerprint(null);
                setModalGenerationErrors({ _form: [nextGeneration.errorMessage || 'Agreement generation failed. Review the saved history before trying again.'] });
            } else {
                setGeneratedContract(nextGeneration);
                setPreviewFingerprint(generationPreviewFingerprint(generationForm));
                setRevisionSource(nextGeneration);
                if (contractId) {
                    setContracts(previous => previous.map(contract => contract.contractId === contractId
                        ? { ...contract, latestGeneration: nextGeneration }
                        : contract));
                }
            }
            if (generationContractMode === 'new') await fetchContracts();
        } catch (requestError: any) {
            console.error('Failed to generate contract', requestError);
            setModalGenerationErrors(apiGenerationErrors(requestError, 'Failed to generate contract'));
        } finally {
            setGeneratingContract(false);
        }
    };

    const handleSendGeneratedContract = async () => {
        if (!generatedContract) return;

        if (previewIsStale) {
            setModalGenerationErrors({ _form: ['The agreement details changed after this preview was generated. Generate a fresh preview before sending.'] });
            return;
        }

        if (!window.confirm('Send this contract through DocuSign now? Gemstone will sign first, then the client signer below.')) {
            return;
        }

        setSendingDocusign(true);
        setGenerationErrors({});
        try {
            const signerName = generationForm.signerName || generationForm.payload.CLIENT_SIGNER_NAME || '';
            const signerEmail = generationForm.signerEmail || generationForm.payload.CLIENT_SIGNER_EMAIL || '';
            const response = await axios.post(`${apiBase}/contract-generations/${generatedContract.id}/send-docusign`, {
                signerName,
                signerEmail,
                emailSubject: generationForm.emailSubject || `Contract for signature: ${generatedContract.outputBasename}`,
            }, authHeaders);
            const sentGeneration = response.data as GeneratedContract;
            setGeneratedContract(sentGeneration);
            if (sentGeneration.contractId) {
                setGenerationHistories(previous => ({
                    ...previous,
                    [sentGeneration.contractId as number]: (previous[sentGeneration.contractId as number] ?? []).map(generation =>
                        generation.id === sentGeneration.id ? sentGeneration : generation
                    ),
                }));
                setContracts(previous => previous.map(contract => contract.contractId === sentGeneration.contractId
                    ? { ...contract, latestGeneration: sentGeneration }
                    : contract));
            }
        } catch (requestError: any) {
            console.error('Failed to send contract through DocuSign', requestError);
            setModalGenerationErrors(apiGenerationErrors(requestError, 'Failed to send contract through DocuSign'));
        } finally {
            setSendingDocusign(false);
        }
    };

    const handleRenewHosting = async (contract: Contract) => {
        setError(null);
        try {
            const response = await axios.post(`${apiBase}/contracts/${contract.contractId}/renew-hosting`, {}, authHeaders);
            const renewedGeneration = response.data as GeneratedContract;
            const fallbackForm = createGenerationFormForContract(contract);
            fallbackForm.contractId = renewedGeneration.contractId ?? contract.contractId;
            fallbackForm.createContract = false;
            setGenerationContractMode('existing');
            setRevisionSource(renewedGeneration);
            setGenerationForm(fallbackForm);
            setGeneratedContract(renewedGeneration);
            setPreviewFingerprint('__renewal-details-not-loaded__');
            setGenerationErrors({});
            setLoadingGenerationDetail(true);
            setGenerationDetailLoadFailed(false);
            setShowGenerationModal(true);

            try {
                const detailResponse = await axios.get(`${apiBase}/contract-generations/${renewedGeneration.id}`, authHeaders);
                const detail = detailResponse.data as GeneratedContract;
                const rehydratedForm = createGenerationFormFromDetail(contract, detail);
                setRevisionSource(detail);
                setGenerationForm(rehydratedForm);
                setGeneratedContract(detail);
                setPreviewFingerprint(generationPreviewFingerprint(rehydratedForm));
                setGenerationDetailLoadFailed(false);
            } catch (detailError: any) {
                console.error(`Failed to load renewed generation ${renewedGeneration.id}`, detailError);
                setGenerationDetailLoadFailed(true);
                setModalGenerationErrors({
                    _form: ['The renewed agreement was generated, but its saved inputs could not be verified. Close this window and reopen it from agreement history before sending.'],
                });
            } finally {
                setLoadingGenerationDetail(false);
            }
            await fetchContracts();
        } catch (requestError: any) {
            console.error('Failed to renew hosting contract', requestError);
            setError(firstApiError(requestError, 'Failed to renew hosting contract'));
        }
    };

    const handleEdit = (contract: Contract) => {
        setEditContractId(contract.contractId);
        setEditFormData({ ...contract, attachment: contract.attachment ?? '' });
        if (contract.freeagentCustomerId) fetchProjectsByContact(contract.freeagentCustomerId);
    };

    const handleCancel = () => { setEditContractId(null); setEditFormData(initialEditFormData); };

    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const target = event.target as HTMLInputElement;
        if (target.type === 'checkbox') {
            setEditFormData(prev => ({ ...prev, [target.name]: target.checked }));
            return;
        }
        if (target.name === 'type') {
            const type = target.value as ContractType;
            setEditFormData(prev => ({
                ...prev,
                type,
                linkedRecurringContractId: type === 'one_off' ? prev.linkedRecurringContractId : null,
                freeagentRecurringInvoiceId: type === 'recurring' ? prev.freeagentRecurringInvoiceId : null,
                hoursPerMonth: type === 'recurring' ? prev.hoursPerMonth : null,
            }));
            return;
        }
        if (target.name === 'linkedRecurringContractId') {
            setEditFormData(prev => ({ ...prev, linkedRecurringContractId: target.value ? Number(target.value) : null }));
            return;
        }
        if (target.name === 'supersededById') {
            setEditFormData(prev => ({ ...prev, supersededById: target.value ? Number(target.value) : null }));
            return;
        }
        if (['freeagentCustomerId', 'freeagentRecurringInvoiceId', 'freeagentProjectId', 'hoursPerMonth', 'totalContractValue'].includes(target.name)) {
            setEditFormData(prev => ({ ...prev, [target.name]: target.value === '' ? 0 : Number(target.value) }));
            return;
        }
        setEditFormData(prev => ({ ...prev, [target.name]: target.value }));
    };

    const handleDateChange = (date: Date | null, field: 'expiryDate' | 'startDate') => {
        setEditFormData(prev => ({ ...prev, [field]: date ? moment(date).format('YYYY-MM-DD') : null }));
    };

    const updateNewDocument = (index: number, field: keyof ContractDocument, value: string) => {
        setNewContractData(prev => ({
            ...prev,
            documents: prev.documents.map((document, documentIndex) =>
                documentIndex === index ? { ...document, [field]: value } : document
            ),
        }));
    };

    const updateEditDocument = (index: number, field: keyof ContractDocument, value: string) => {
        setEditFormData(prev => ({
            ...prev,
            documents: (prev.documents ?? []).map((document, documentIndex) =>
                documentIndex === index ? { ...document, [field]: value } : document
            ),
        }));
    };

    const addNewDocument = () => {
        setNewContractData(prev => ({ ...prev, documents: [...prev.documents, emptyDocument()] }));
    };

    const addEditDocument = () => {
        setEditFormData(prev => ({ ...prev, documents: [...(prev.documents ?? []), emptyDocument()] }));
    };

    const removeNewDocument = (index: number) => {
        setNewContractData(prev => ({ ...prev, documents: prev.documents.filter((_, documentIndex) => documentIndex !== index) }));
    };

    const removeEditDocument = (index: number) => {
        setEditFormData(prev => ({ ...prev, documents: (prev.documents ?? []).filter((_, documentIndex) => documentIndex !== index) }));
    };

    const handleCreateContract = async () => {
        try {
            const response = await axios.post(`${apiBase}/contracts`, {
                ...newContractData,
                type: newContractData.type,
                linkedRecurringContractId: newContractData.type === 'one_off' && newContractData.linkedRecurringContractId ? Number(newContractData.linkedRecurringContractId) : null,
                freeagentCustomerId: Number(newContractData.freeagentCustomerId) || 0,
                freeagentRecurringInvoiceId: newContractData.type === 'recurring' ? Number(newContractData.freeagentRecurringInvoiceId) || 0 : null,
                freeagentProjectId: newContractData.freeagentProjectId ? Number(newContractData.freeagentProjectId) : null,
                hoursPerMonth: newContractData.type === 'recurring' ? Number(newContractData.hoursPerMonth) || 0 : null,
                totalContractValue: Number(newContractData.totalContractValue) || 0,
                attachment: normalizeAttachment(newContractData.attachment),
                expiryDate: newContractData.expiryDate ? moment(newContractData.expiryDate).format('YYYY-MM-DD') : null,
                startDate: newContractData.startDate ? moment(newContractData.startDate).format('YYYY-MM-DD') : null,
                supersededById: newContractData.supersededById ? Number(newContractData.supersededById) : null,
                documents: normalizeDocuments(newContractData.documents),
            }, authHeaders);
            if (response.status === 201) {
                handleCloseModal();
                await fetchContracts();
            }
        } catch (requestError) {
            console.error('Failed to create contract', requestError);
            setError('Failed to create contract');
        }
    };

    const handleSave = async (contractId: number) => {
        setUpdatingContract(true);
        try {
            const payload = {
                ...editFormData,
                type: editFormData.type,
                linkedRecurringContractId: editFormData.type === 'one_off' ? editFormData.linkedRecurringContractId : null,
                freeagentCustomerId: Number(editFormData.freeagentCustomerId) || 0,
                freeagentRecurringInvoiceId: editFormData.type === 'recurring' ? Number(editFormData.freeagentRecurringInvoiceId) || 0 : null,
                freeagentProjectId: editFormData.freeagentProjectId ? Number(editFormData.freeagentProjectId) : null,
                hoursPerMonth: editFormData.type === 'recurring' ? Number(editFormData.hoursPerMonth) || 0 : null,
                totalContractValue: Number(editFormData.totalContractValue) || 0,
                attachment: normalizeAttachment(editFormData.attachment),
                supersededById: editFormData.supersededById ? Number(editFormData.supersededById) : null,
                documents: normalizeDocuments(editFormData.documents ?? []),
            };
            const response = await axios.put(`${apiBase}/contracts/${contractId}`, payload, authHeaders);
            if (response.status === 200) {
                setContracts(prev => prev.map(contract => contract.contractId === contractId ? {
                    ...contract,
                    ...payload,
                    attachment: payload.attachment,
                    documents: payload.documents,
                } : contract));
            } else {
                setError('Failed to update contract');
            }
        } catch (requestError) {
            console.error('Failed to update contract', requestError);
            setError('Failed to update contract');
        } finally {
            setUpdatingContract(false);
            setEditContractId(null);
        }
    };

    const handleSort = (field: SortField) => {
        setSortDirection(prev => sortField === field && prev === 'asc' ? 'desc' : 'asc');
        setSortField(field);
    };

    const renderSortLabel = (label: string, field: SortField) => `${label}${sortField === field ? (sortDirection === 'asc' ? ' ASC' : ' DESC') : ''}`;

    const renderFreeAgentLink = (value: number | null | undefined, path: string) => {
        if (!value) return 'Not set';
        return <a className="contract-link" href={`https://gemstoneitservicesltd.freeagent.com/${path}/${value}`} target="_blank" rel="noopener noreferrer">{value}</a>;
    };

    const renderDocumentLinks = (contract: Contract) => {
        const documents = contract.documents ?? [];
        if (documents.length > 0) {
            return (
                <div className="contract-document-list">
                    {documents.slice(0, 3).map(document => (
                        <a className="contract-link" key={document.documentId ?? document.sharepointUrl} href={document.sharepointUrl} target="_blank" rel="noopener noreferrer">
                            {document.title || 'Open document'}
                        </a>
                    ))}
                    {documents.length > 3 && <span className="text-muted">+{documents.length - 3} more</span>}
                </div>
            );
        }

        if (contract.attachment) {
            return /^https?:\/\//i.test(contract.attachment)
                ? <a className="contract-link" href={contract.attachment} target="_blank" rel="noopener noreferrer">Legacy attachment</a>
                : contract.attachment;
        }

        return 'None';
    };

    const renderContractDetail = (label: string, value: React.ReactNode) => (
        <div className="contract-detail">
            <span className="contract-detail__label">{label}</span>
            <span className="contract-detail__value">{value}</span>
        </div>
    );

    const renderGenerationLinks = (generation?: GeneratedContract | null) => {
        if (!generation) return 'None';

        const links = generation.documents
            .filter(document => document.sharepointWebUrl)
            .map(document => (
                <a className="contract-link" key={document.id} href={document.sharepointWebUrl ?? '#'} target="_blank" rel="noopener noreferrer">
                    {document.type.toUpperCase()}
                </a>
            ));

        return (
            <div className="contract-document-list">
                <span>{generation.referenceNumber} · {generation.status}</span>
                {links.length > 0 && <span className="contract-generation-links">{links}</span>}
            </div>
        );
    };

    const renderGenerationHistory = (contract: Contract) => {
        const generations = generationHistories[contract.contractId]
            ?? (contract.latestGeneration ? [contract.latestGeneration] : []);
        const historyError = generationHistoryErrors[contract.contractId];

        return (
            <section className="contract-generation-history" aria-label={`${contract.customerName} agreement history`}>
                <div className="contract-section__header">
                    <h4>Agreement history</h4>
                    <p>Every generated agreement remains available; revising creates a new immutable version.</p>
                </div>
                {historyError && <Alert variant="warning" className="py-2 mb-2">{historyError}</Alert>}
                {generations.length === 0 ? (
                    <p className="text-muted mb-0">No agreements have been generated.</p>
                ) : (
                    <ol className="contract-generation-history__list">
                        {generations.map(generation => (
                            <li key={generation.id} className="contract-generation-history__item">
                                <div>
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        <strong>{generation.referenceNumber || `Generation ${generation.id}`}</strong>
                                        {contract.latestGeneration?.id === generation.id && <Badge bg="success">Latest</Badge>}
                                        <Badge bg={generation.status === 'failed' ? 'danger' : 'secondary'}>{generation.status}</Badge>
                                    </div>
                                    <time dateTime={generation.createdAt ?? undefined} className="text-muted small">
                                        {generation.createdAt ? moment(generation.createdAt).format('DD MMM YYYY, HH:mm') : 'Date unavailable'}
                                    </time>
                                    {generation.errorMessage && <div className="text-danger small mt-1">{generation.errorMessage}</div>}
                                </div>
                                <div className="contract-generation-history__actions">
                                    {(generation.documents ?? []).filter(document => document.sharepointWebUrl).map(document => (
                                        <Button
                                            key={document.id}
                                            size="sm"
                                            variant="outline-secondary"
                                            href={document.sharepointWebUrl ?? undefined}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={`Open ${document.type.toUpperCase()} for ${generation.referenceNumber}`}
                                        >
                                            {document.type.toUpperCase()}
                                        </Button>
                                    ))}
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => handleOpenRevision(contract, generation)}
                                        aria-label={`${generationIsReadyToSend(generation) ? 'Review or send' : 'Revise'} ${generation.referenceNumber || `generation ${generation.id}`}`}
                                    >
                                        {generationIsReadyToSend(generation) ? 'Review / Send' : 'Revise'}
                                    </Button>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        );
    };

    const renderDocumentEditor = (
        documents: ContractDocument[],
        updateDocument: (index: number, field: keyof ContractDocument, value: string) => void,
        addDocument: () => void,
        removeDocument: (index: number) => void
    ) => (
        <div className="contract-document-editor">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="h6 mb-0">SharePoint Documents</h3>
                <Button type="button" variant="outline-primary" size="sm" onClick={addDocument}>Add Document</Button>
            </div>
            {documents.length === 0 ? (
                <p className="text-muted mb-0">No SharePoint documents linked.</p>
            ) : documents.map((document, index) => (
                <div key={document.documentId ?? index} className="contract-document-editor__item">
                    <div className="row">
                        <Form.Group className="mb-2 col-md-5">
                            <Form.Label>Title</Form.Label>
                            <Form.Control value={document.title} onChange={(event) => updateDocument(index, 'title', event.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-2 col-md-5">
                            <Form.Label>SharePoint URL</Form.Label>
                            <Form.Control value={document.sharepointUrl} onChange={(event) => updateDocument(index, 'sharepointUrl', event.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-2 col-md-2">
                            <Form.Label>Type</Form.Label>
                            <Form.Control value={document.documentType ?? ''} onChange={(event) => updateDocument(index, 'documentType', event.target.value)} />
                        </Form.Group>
                    </div>
                    <Form.Group className="mb-2">
                        <Form.Label>Notes</Form.Label>
                        <Form.Control value={document.notes ?? ''} onChange={(event) => updateDocument(index, 'notes', event.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label>Markdown Content</Form.Label>
                        <Form.Control as="textarea" rows={4} value={document.markdownContent ?? ''} onChange={(event) => updateDocument(index, 'markdownContent', event.target.value)} />
                    </Form.Group>
                    <Button type="button" variant="outline-danger" size="sm" onClick={() => removeDocument(index)}>Remove Document</Button>
                </div>
            ))}
        </div>
    );

    const renderContractCard = (contract: Contract) => {
        const isEditing = editContractId === contract.contractId;
        const isOneOff = contract.type === 'one_off';
        const documentCount = contract.documents?.length ?? 0;
        const rowIsOverdue = isActiveAndExpired(contract);
        const revisionCandidate = revisionCandidateForContract(contract);

        return (
            <Col key={contract.contractId} xs={12}>
                <Card className={`contract-card h-100 border-0 shadow-sm${rowIsOverdue ? ' contract-card--warning' : ''}`}>
                    <Card.Body>
                        <div className="contract-card__hero">
                            <div>
                                <p className="contract-card__eyebrow mb-2">
                                    Contract #{contract.contractId} · {formatContractType(contract.type)}
                                </p>
                                <h2 className="contract-card__title">
                                    {isEditing ? (
                                        <Form.Control name="customerName" value={editFormData.customerName} onChange={handleFormChange} />
                                    ) : contract.customerName}
                                </h2>
                                <p className="contract-card__meta mb-0">
                                    {isOneOff ? 'One-off project contract' : 'Recurring hosting or retainer contract'}
                                </p>
                            </div>
                            <Stack direction="horizontal" gap={2} className="contract-card__badges flex-wrap">
                                <Badge bg={isOneOff ? 'info' : 'secondary'}>{formatContractType(contract.type)}</Badge>
                                <Badge bg={contract.status === 'active' ? 'success' : 'light'} text={contract.status === 'active' ? undefined : 'dark'}>{contract.status}</Badge>
                                <Badge bg={documentCount > 0 ? 'warning' : 'secondary'} text={documentCount > 0 ? 'dark' : undefined}>
                                    {documentCount > 0 ? `${documentCount} document${documentCount === 1 ? '' : 's'}` : 'No documents'}
                                </Badge>
                            </Stack>
                        </div>

                        {isEditing ? (
                            <div className="contract-edit-grid">
                                <Form.Group>
                                    <Form.Label>Type</Form.Label>
                                    <Form.Select name="type" value={editFormData.type} onChange={handleFormChange}>
                                        <option value="recurring">Recurring / hosting</option>
                                        <option value="one_off">One-off project</option>
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Status</Form.Label>
                                    <Form.Select name="status" value={editFormData.status} onChange={handleFormChange}>
                                        {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>FreeAgent Customer</Form.Label>
                                    <Form.Select name="freeagentCustomerId" value={editFormData.freeagentCustomerId} onChange={handleContactChange}>
                                        <option value={0}>Select a customer</option>
                                        {freeAgentContacts.map(contact => {
                                            const lastSegment = contact.url.split('/').pop() ?? String(contact.id);
                                            return <option key={lastSegment} value={lastSegment}>{contactDisplayName(contact)}</option>;
                                        })}
                                    </Form.Select>
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>FreeAgent Project{editFormData.type === 'one_off' ? ' (required)' : ''}</Form.Label>
                                    <Form.Select name="freeagentProjectId" value={editFormData.freeagentProjectId ?? ''} onChange={handleFormChange} disabled={projectsLoading}>
                                        <option value="">{projectsLoading ? 'Loading projects...' : 'Select a project'}</option>
                                        {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                                    </Form.Select>
                                </Form.Group>
                                {editFormData.type === 'recurring' ? (
                                    <>
                                        <Form.Group>
                                            <Form.Label>Recurring Invoice ID</Form.Label>
                                            <Form.Control type="number" name="freeagentRecurringInvoiceId" value={editFormData.freeagentRecurringInvoiceId ?? ''} onChange={handleFormChange} />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label>Hours Per Month</Form.Label>
                                            <Form.Control type="number" step="0.01" name="hoursPerMonth" value={editFormData.hoursPerMonth ?? ''} onChange={handleFormChange} />
                                        </Form.Group>
                                    </>
                                ) : (
                                    <Form.Group>
                                        <Form.Label>Linked Hosting / Recurring Contract</Form.Label>
                                        <Form.Select name="linkedRecurringContractId" value={editFormData.linkedRecurringContractId ?? ''} onChange={handleFormChange}>
                                            <option value="">None</option>
                                            {recurringContracts
                                                .filter(other => other.contractId !== contract.contractId)
                                                .map(other => <option key={other.contractId} value={other.contractId}>{other.customerName} (#{other.contractId})</option>)}
                                        </Form.Select>
                                    </Form.Group>
                                )}
                                <Form.Group>
                                    <Form.Label>Total Contract Value</Form.Label>
                                    <Form.Control type="number" step="0.01" name="totalContractValue" value={editFormData.totalContractValue ?? ''} onChange={handleFormChange} />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <DatePicker selected={parseDate(editFormData.startDate)} onChange={(date) => handleDateChange(date, 'startDate')} className="form-control" isClearable />
                                </Form.Group>
                                <Form.Group>
                                    <Form.Label>Expiry Date</Form.Label>
                                    <DatePicker selected={parseDate(editFormData.expiryDate)} onChange={(date) => handleDateChange(date, 'expiryDate')} className="form-control" isClearable />
                                </Form.Group>
                                <Form.Group className="contract-edit-grid__wide">
                                    <Form.Label>Notes</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="notes" value={editFormData.notes ?? ''} onChange={handleFormChange} />
                                </Form.Group>
                                <Form.Group className="contract-edit-grid__wide">
                                    <Form.Label>Legacy Attachment</Form.Label>
                                    <Form.Control name="attachment" value={editFormData.attachment ?? ''} onChange={handleFormChange} placeholder="Attachment file or URL" />
                                </Form.Group>
                                <div className="contract-edit-grid__wide">
                                    {renderDocumentEditor(editFormData.documents ?? [], updateEditDocument, addEditDocument, removeEditDocument)}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="contract-summary-grid">
                                    {renderContractDetail('FreeAgent Customer', renderFreeAgentLink(contract.freeagentCustomerId, 'contacts'))}
                                    {renderContractDetail('FreeAgent Project', renderFreeAgentLink(contract.freeagentProjectId, 'projects'))}
                                    {renderContractDetail('Start Date', formatDate(contract.startDate))}
                                    {renderContractDetail('Expiry Date', formatDate(contract.expiryDate))}
                                    {renderContractDetail('Value', `£${(Number(contract.totalContractValue) || 0).toFixed(2)}`)}
                                    {renderContractDetail('Reminders', `${contract.remindSupplier ? 'Supplier' : 'No supplier'} / ${contract.remindCustomer ? 'Customer' : 'No customer'}`)}
                                </div>

                                <div className="contract-section">
                                    <div className="contract-section__header">
                                        <h3>{isOneOff ? 'Project Linkage' : 'Recurring Commercials'}</h3>
                                        <p>{isOneOff ? 'FreeAgent project and hosting contract relationships.' : 'Recurring invoice and monthly support allowance.'}</p>
                                    </div>
                                    <div className="contract-detail-grid">
                                        {isOneOff ? (
                                            <>
                                                {renderContractDetail('Linked Hosting Contract', contract.linkedRecurringContract ? `${contract.linkedRecurringContract.customerName} (#${contract.linkedRecurringContract.contractId})` : 'None')}
                                                {renderContractDetail('FreeAgent Project', renderFreeAgentLink(contract.freeagentProjectId, 'projects'))}
                                            </>
                                        ) : (
                                            <>
                                                {renderContractDetail('Recurring Invoice', renderFreeAgentLink(contract.freeagentRecurringInvoiceId, 'invoices/recurring'))}
                                                {renderContractDetail('Hours Per Month', contract.hoursPerMonth ?? 'Not set')}
                                            </>
                                        )}
                                    </div>
                                </div>

                                <div className="contract-section contract-section--documents">
                                    <div className="contract-section__header">
                                        <h3>Documents</h3>
                                        <p>Linked SharePoint agreements, PDFs, scopes, and extracted Markdown content.</p>
                                    </div>
                                    <div className="contract-detail-grid">
                                        {renderContractDetail('SharePoint Links', renderDocumentLinks(contract))}
                                        {renderContractDetail('Markdown Captured', (contract.documents ?? []).some(document => Boolean(document.markdownContent)) ? 'Yes' : 'No')}
                                        {renderContractDetail('Latest Generated Agreement', renderGenerationLinks(contract.latestGeneration))}
                                        {renderContractDetail('DocuSign Status', contract.latestGeneration?.docusignEnvelope?.status || 'Not sent')}
                                    </div>
                                    {renderGenerationHistory(contract)}
                                </div>

                                {contract.notes && <div className="contract-notes">{contract.notes}</div>}
                            </>
                        )}

                        <div className="contract-action-row">
                            {isEditing ? (
                                <>
                                    <Button type="button" size="sm" variant="primary" onClick={() => handleSave(contract.contractId)} disabled={updatingContract}>Save</Button>
                                    <Button type="button" size="sm" variant="outline-secondary" onClick={handleCancel}>Cancel</Button>
                                </>
                            ) : (
                                <>
                                    <Button type="button" size="sm" variant="primary" onClick={() => handleEdit(contract)}>Edit Contract</Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => revisionCandidate
                                            ? handleOpenRevision(contract, revisionCandidate)
                                            : handleOpenGenerationModal(contract)}
                                    >
                                        {generationIsReadyToSend(revisionCandidate) ? 'Review / Send Agreement' : revisionCandidate ? 'Revise Agreement' : 'Generate Agreement'}
                                    </Button>
                                    {!isOneOff && <Button type="button" size="sm" variant="outline-secondary" onClick={() => handleRenewHosting(contract)}>Renew Hosting</Button>}
                                </>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        );
    };

    const recurringCount = contracts.filter(contract => contract.type === 'recurring').length;
    const oneOffCount = contracts.filter(contract => contract.type === 'one_off').length;
    const documentsCount = contracts.reduce((total, contract) => total + (contract.documents?.length ?? 0), 0);
    const expiringCount = contracts.filter(contract => contract.expiryDate && moment(contract.expiryDate).isSameOrBefore(moment().add(60, 'days'), 'day')).length;
    const effectiveSignerName = generationForm.signerName || generationForm.payload.CLIENT_SIGNER_NAME || '';
    const effectiveSignerEmail = generationForm.signerEmail || generationForm.payload.CLIENT_SIGNER_EMAIL || '';
    const generationFieldErrors = (...keys: string[]) => keys.flatMap(key => allGenerationErrors[key] ?? []);
    const generationFieldIsInvalid = (...keys: string[]) => generationFieldErrors(...keys).length > 0;
    const generationErrorList = Object.entries(allGenerationErrors).flatMap(([key, messages]) =>
        messages.map(message => ({ key, message }))
    );
    const selectedGenerationContract = contracts.find(contract => contract.contractId === Number(generationForm.contractId));

    return (
        <div className="contract-manager-page">
            <div className="container py-4 py-lg-5">
                <section className="contract-manager-hero">
                    <div>
                        <p className="contract-manager-hero__eyebrow">Operations Console</p>
                        <h1>Contract Manager</h1>
                        <p className="contract-manager-hero__copy">
                            Manage hosting retainers, one-off website jobs, FreeAgent links, generated agreements, and the SharePoint documents that evidence each contract.
                        </p>
                    </div>
                    <div className="contract-manager-hero__actions">
                        <Button variant="light" onClick={handleOpenModal}>Create Contract</Button>
                        <Button variant="outline-light" onClick={() => handleOpenGenerationModal()} disabled={templatesLoading}>
                            {templatesLoading ? 'Loading Templates...' : 'Generate Agreement'}
                        </Button>
                    </div>
                </section>

                <Row className="g-3 contract-manager-stats">
                    <Col sm={6} xl={3}><Card className="contract-manager-stat border-0 shadow-sm"><Card.Body><span>Total Contracts</span><strong>{contracts.length}</strong></Card.Body></Card></Col>
                    <Col sm={6} xl={3}><Card className="contract-manager-stat border-0 shadow-sm"><Card.Body><span>Recurring</span><strong>{recurringCount}</strong></Card.Body></Card></Col>
                    <Col sm={6} xl={3}><Card className="contract-manager-stat border-0 shadow-sm"><Card.Body><span>One-off</span><strong>{oneOffCount}</strong></Card.Body></Card></Col>
                    <Col sm={6} xl={3}><Card className="contract-manager-stat border-0 shadow-sm"><Card.Body><span>Documents</span><strong>{documentsCount}</strong></Card.Body></Card></Col>
                </Row>

                <section className="contract-manager-toolbar">
                    <div>
                        <h2>Contract Inventory</h2>
                        <p className="mb-0 text-muted">Showing {filteredAndSortedContracts.length} of {contracts.length} contracts. {expiringCount} expire in the next 60 days.</p>
                    </div>
                    <Form.Control
                        className="contract-manager-search"
                        placeholder="Search by client, type, status, document, FreeAgent ID, or notes"
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                    />
                </section>

                <section className="contract-manager-filters">
                    <div className="contract-filter-group">
                        <span>Type</span>
                        {typeOptions.map(type => (
                            <Form.Check key={type} type="switch" id={`type-${type}`} label={formatContractType(type)} checked={typeFilters[type]} onChange={(event) => setTypeFilters(prev => ({ ...prev, [type]: event.currentTarget.checked }))} />
                        ))}
                    </div>
                    <div className="contract-filter-group">
                        <span>Status</span>
                        {statusOptions.map(status => (
                            <Form.Check key={status} type="switch" id={`status-${status}`} label={status.charAt(0).toUpperCase() + status.slice(1)} checked={statusFilters[status]} onChange={(event) => setStatusFilters(prev => ({ ...prev, [status]: event.currentTarget.checked }))} />
                        ))}
                    </div>
                    <div className="contract-sort-group">
                        <Button variant="link" onClick={() => handleSort('customerName')}>{renderSortLabel('Name', 'customerName')}</Button>
                        <Button variant="link" onClick={() => handleSort('type')}>{renderSortLabel('Type', 'type')}</Button>
                        <Button variant="link" onClick={() => handleSort('expiryDate')}>{renderSortLabel('Expiry', 'expiryDate')}</Button>
                        <Button variant="link" onClick={() => handleSort('totalContractValue')}>{renderSortLabel('Value', 'totalContractValue')}</Button>
                    </div>
                </section>

                {error && <Alert variant="danger">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : filteredAndSortedContracts.length === 0 ? (
                    <Card className="contract-empty-state border-0 shadow-sm">
                        <Card.Body>
                            <h2>No contracts found</h2>
                            <p className="mb-0 text-muted">Try a different search or filter combination.</p>
                        </Card.Body>
                    </Card>
                ) : (
                    <Row className="g-4">
                        {filteredAndSortedContracts.map(renderContractCard)}
                    </Row>
                )}
            </div>

            <Modal show={showModal} onHide={handleCloseModal} size="xl">
                <Modal.Header closeButton><Modal.Title>Create Contract</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form>
                        <div className="row">
                            <Form.Group className="mb-3 col-md-4">
                                <Form.Label>Type</Form.Label>
                                <Form.Select name="type" value={newContractData.type} onChange={handleNewContractChange}>
                                    <option value="recurring">Recurring / hosting</option>
                                    <option value="one_off">One-off project</option>
                                </Form.Select>
                            </Form.Group>
                            <Form.Group className="mb-3 col-md-4">
                                <Form.Label>Status</Form.Label>
                                <Form.Control as="select" name="status" value={newContractData.status} onChange={handleNewContractChange}>
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="superseded">Superseded</option>
                                </Form.Control>
                            </Form.Group>
                            <Form.Group className="mb-3 col-md-4">
                                <Form.Label>Total Contract Value</Form.Label>
                                <Form.Control type="number" step="0.01" name="totalContractValue" value={newContractData.totalContractValue} onChange={handleNewContractChange} />
                            </Form.Group>
                        </div>
                        <Form.Group className="mb-3">
                            <Form.Label>FreeAgent Customer ID</Form.Label>
                            <Form.Control as="select" name="freeagentCustomerId" value={newContractData.freeagentCustomerId} onChange={handleContactChange}>
                                <option value={0}>Select a customer</option>
                                {freeAgentContacts.map(contact => {
                                    const lastSegment = contact.url.split('/').pop() ?? String(contact.id);
                                    return <option key={lastSegment} value={lastSegment}>{contact.organisationName || `${contact.firstName} ${contact.lastName}`}</option>;
                                })}
                            </Form.Control>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Project</Form.Label>
                            <Form.Control as="select" name="freeagentProjectId" value={newContractData.freeagentProjectId} onChange={e => setNewContractData(prev => ({ ...prev, freeagentProjectId: Number(e.target.value) || 0 }))} disabled={projectsLoading}>
                                <option value={0}>{projectsLoading ? 'Loading projects...' : 'Select a Project'}</option>
                                {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                            </Form.Control>
                            {projectsError && <div className="text-danger">{projectsError}</div>}
                        </Form.Group>
                        <Form.Group className="mb-3"><Form.Label>Customer Contract Name</Form.Label><Form.Control type="text" name="customerName" value={newContractData.customerName} onChange={handleNewContractChange} /></Form.Group>
                        {newContractData.type === 'recurring' ? (
                            <div className="row">
                                <Form.Group className="mb-3 col-md-6"><Form.Label>FreeAgent Recurring Invoice ID</Form.Label><Form.Control type="number" name="freeagentRecurringInvoiceId" value={newContractData.freeagentRecurringInvoiceId} onChange={handleNewContractChange} /></Form.Group>
                                <Form.Group className="mb-3 col-md-6"><Form.Label>Hours Per Month</Form.Label><Form.Control type="number" step="0.01" name="hoursPerMonth" value={newContractData.hoursPerMonth} onChange={handleNewContractChange} /></Form.Group>
                            </div>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>Linked Hosting / Recurring Contract</Form.Label>
                                <Form.Select name="linkedRecurringContractId" value={newContractData.linkedRecurringContractId} onChange={handleNewContractChange}>
                                    <option value="">None</option>
                                    {recurringContracts.map(contract => <option key={contract.contractId} value={contract.contractId}>{contract.customerName} (#{contract.contractId})</option>)}
                                </Form.Select>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3"><Form.Label>Notes</Form.Label><Form.Control as="textarea" rows={3} name="notes" value={newContractData.notes} onChange={handleNewContractChange} /></Form.Group>
                        <Form.Group className="mb-3"><Form.Label>Attachment</Form.Label><Form.Control type="text" name="attachment" value={newContractData.attachment} onChange={handleNewContractChange} placeholder="Attachment file or URL" /></Form.Group>
                        <div className="row">
                            <Form.Group className="mb-3 col-md-6"><Form.Label>Expiry Date</Form.Label><DatePicker selected={newContractData.expiryDate} onChange={(date) => handleNewContractDateChange('expiryDate', date)} className="form-control" isClearable /></Form.Group>
                            <Form.Group className="mb-3 col-md-6"><Form.Label>Start Date</Form.Label><DatePicker selected={newContractData.startDate} onChange={(date) => handleNewContractDateChange('startDate', date)} className="form-control" isClearable /></Form.Group>
                        </div>
                        {renderDocumentEditor(newContractData.documents, updateNewDocument, addNewDocument, removeNewDocument)}
                        <div className="d-flex flex-wrap gap-3 mt-3">
                            <Form.Check type="checkbox" label="Will terminate on expiry" name="willTerminateOnExpiry" checked={newContractData.willTerminateOnExpiry} onChange={handleNewContractChange} />
                            <Form.Check type="checkbox" label="Supplier reminders" name="remindSupplier" checked={newContractData.remindSupplier} onChange={handleNewContractChange} />
                            <Form.Check type="checkbox" label="Customer reminders" name="remindCustomer" checked={newContractData.remindCustomer} onChange={handleNewContractChange} />
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
                    <Button variant="primary" onClick={handleCreateContract}>Submit</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showGenerationModal} onHide={handleCloseGenerationModal} size="xl" aria-labelledby="agreement-modal-title">
                <Modal.Header closeButton>
                    <Modal.Title id="agreement-modal-title">{revisionSource ? 'Revise Agreement' : 'Generate Agreement'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingGenerationDetail ? (
                        <div className="text-center py-5" role="status">
                            <Spinner animation="border" />
                            <span className="visually-hidden">Loading agreement details</span>
                        </div>
                    ) : (
                        <>
                            {generatedContract && (
                                <Alert variant="success">
                                    <div><strong>{generatedContract.referenceNumber}</strong> generated and selected as the latest agreement.</div>
                                    <div className="d-flex flex-wrap gap-2 mt-2">
                                        {(generatedContract.documents ?? []).map(document => (
                                            document.sharepointWebUrl ? (
                                                <Button key={document.id} size="sm" variant="outline-success" href={document.sharepointWebUrl} target="_blank" rel="noopener noreferrer">
                                                    Open {document.type.toUpperCase()}
                                                </Button>
                                            ) : null
                                        ))}
                                    </div>
                                    {generatedContract.docusignEnvelope && (
                                        <div className="mt-2">DocuSign: {generatedContract.docusignEnvelope.status || generatedContract.docusignEnvelope.envelopeId}</div>
                                    )}
                                </Alert>
                            )}

                            {previewIsStale && (
                                <Alert variant="warning" role="status">
                                    Agreement or signer details changed after this preview was generated. Generate a fresh preview before sending with DocuSign.
                                </Alert>
                            )}

                            {generationErrorList.length > 0 && (
                                <Alert variant="danger" role="alert" className="generation-validation-summary">
                                    <strong>The agreement needs attention:</strong>
                                    <ul className="mb-0 mt-2">
                                        {generationErrorList.map(({ key, message }, index) => <li key={`${key}-${index}`}>{message}</li>)}
                                    </ul>
                                </Alert>
                            )}

                            {revisionSource ? (
                                <Alert variant="info">
                                    Creating an immutable revision of <strong>{revisionSource.referenceNumber || `generation ${revisionSource.id}`}</strong>
                                    {selectedGenerationContract ? ` for ${selectedGenerationContract.customerName}` : ''}. The original files remain unchanged.
                                </Alert>
                            ) : (
                                <fieldset className="generation-contract-mode mb-4">
                                    <legend className="h6">Agreement target</legend>
                                    <p className="text-muted small">Choose explicitly whether this belongs to a contract already in GCM or should create a new pending contract.</p>
                                    <Form.Check
                                        inline
                                        type="radio"
                                        id="generation-existing-contract-mode"
                                        name="generation-contract-mode"
                                        label="Existing Contract"
                                        checked={generationContractMode === 'existing'}
                                        onChange={() => handleGenerationContractModeChange('existing')}
                                    />
                                    <Form.Check
                                        inline
                                        type="radio"
                                        id="generation-new-contract-mode"
                                        name="generation-contract-mode"
                                        label="Create New Contract"
                                        checked={generationContractMode === 'new'}
                                        onChange={() => handleGenerationContractModeChange('new')}
                                    />
                                </fieldset>
                            )}

                            <Form ref={generationFormRef} noValidate>
                                <div className="row">
                                    <Form.Group controlId="generation-template-family" className="mb-3 col-md-3">
                                        <Form.Label>Agreement Type</Form.Label>
                                        <Form.Select
                                            value={generationForm.templateFamily}
                                            onChange={handleGenerationFamilyChange}
                                            isInvalid={generationFieldIsInvalid('templateFamily')}
                                            data-generation-field="templateFamily"
                                            aria-invalid={generationFieldIsInvalid('templateFamily')}
                                        >
                                            <option value="">Select a type</option>
                                            {families.map(family => <option key={family} value={family}>{formatFamilyLabel(family)}</option>)}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('templateFamily')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="generation-template-key" className="mb-3 col-md-5">
                                        <Form.Label>Template</Form.Label>
                                        <Form.Select
                                            value={generationForm.templateKey}
                                            onChange={handleGenerationTemplateChange}
                                            disabled={!generationForm.templateFamily}
                                            isInvalid={generationFieldIsInvalid('templateKey')}
                                            data-generation-field="templateKey"
                                            aria-invalid={generationFieldIsInvalid('templateKey')}
                                        >
                                            <option value="">Select a template</option>
                                            {selectedFamilyTemplates.map(template => (
                                                <option key={template.key} value={template.key}>{template.name}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('templateKey')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="generation-template-version" className="mb-3 col-md-4">
                                        <Form.Label>Version</Form.Label>
                                        <Form.Select
                                            value={generationForm.templateVersionId}
                                            onChange={handleGenerationVersionChange}
                                            disabled={!selectedGenerationTemplate}
                                            isInvalid={generationFieldIsInvalid('templateVersionId')}
                                            data-generation-field="templateVersionId"
                                            aria-invalid={generationFieldIsInvalid('templateVersionId')}
                                        >
                                            <option value={0}>Select a version</option>
                                            {templateVersions(selectedGenerationTemplate)
                                                .filter(version => version.id === selectedGenerationTemplate?.activeVersion?.id || version.id === Number(generationForm.templateVersionId))
                                                .map(version => (
                                                <option key={version.id} value={version.id}>{version.version}{version.id === selectedGenerationTemplate?.activeVersion?.id ? ' (active)' : ''}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('templateVersionId')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </div>

                                {!revisionSource && generationContractMode === 'existing' && (
                                    <Form.Group controlId="generation-existing-contract" className="mb-4">
                                        <Form.Label>Existing Contract</Form.Label>
                                        <Form.Select
                                            value={generationForm.contractId}
                                            onChange={handleGenerationExistingContractChange}
                                            isInvalid={generationFieldIsInvalid('contractId')}
                                            data-generation-field="contractId"
                                            aria-invalid={generationFieldIsInvalid('contractId')}
                                        >
                                            <option value="">Select an existing contract</option>
                                            {contracts
                                                .filter(contract => !revisionCandidateForContract(contract))
                                                .map(contract => <option key={contract.contractId} value={contract.contractId}>{contract.customerName}</option>)}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('contractId')[0]}</Form.Control.Feedback>
                                        {contracts.some(contract => Boolean(revisionCandidateForContract(contract))) && (
                                            <Form.Text muted>Contracts with agreement history must be opened with Revise Agreement so their immutable lineage is preserved.</Form.Text>
                                        )}
                                    </Form.Group>
                                )}

                                {!revisionSource && generationContractMode === 'new' && (
                                    <section className="generation-new-contract" aria-labelledby="generation-new-contract-heading">
                                        <h3 id="generation-new-contract-heading" className="h6 mb-3">New pending contract details</h3>
                                        <div className="row">
                                            <Form.Group controlId="generation-contract-type" className="mb-3 col-md-6">
                                                <Form.Label>Contract Type</Form.Label>
                                                <Form.Select value={generationForm.contract.type} onChange={handleGenerationContractTypeChange}>
                                                    <option value="recurring">Recurring / hosting</option>
                                                    <option value="one_off">One-off project</option>
                                                </Form.Select>
                                            </Form.Group>
                                            {generationForm.contract.type === 'one_off' && (
                                                <Form.Group controlId="generation-linked-recurring-contract" className="mb-3 col-md-6">
                                                    <Form.Label>Existing Master / Recurring Contract</Form.Label>
                                                    <Form.Select value={generationForm.contract.linkedRecurringContractId} onChange={handleGenerationLinkedContractChange}>
                                                        <option value="">None — standalone project agreement</option>
                                                        {recurringContracts
                                                            .filter(contract => !generationForm.freeagentContactId || contract.freeagentCustomerId === generationForm.freeagentContactId)
                                                            .map(contract => (
                                                            <option key={contract.contractId} value={contract.contractId}>{contract.customerName} (#{contract.contractId})</option>
                                                        ))}
                                                    </Form.Select>
                                                    <Form.Text muted>Selecting a parent agreement recommends the Statement of Work template.</Form.Text>
                                                </Form.Group>
                                            )}
                                        </div>
                                        <div className="row">
                                            <Form.Group controlId="generation-freeagent-client" className="mb-3 col-md-6">
                                                <Form.Label>FreeAgent Client</Form.Label>
                                                <Form.Select
                                                    value={generationForm.freeagentContactId}
                                                    onChange={handleGenerationContactChange}
                                                    isInvalid={generationFieldIsInvalid('freeagentContactId')}
                                                    data-generation-field="freeagentContactId"
                                                    aria-invalid={generationFieldIsInvalid('freeagentContactId')}
                                                >
                                                    <option value={0}>Select a customer</option>
                                                    {freeAgentContacts.map(contact => {
                                                        const lastSegment = contact.url.split('/').pop() ?? String(contact.id);
                                                        return <option key={lastSegment} value={lastSegment}>{contactDisplayName(contact)}</option>;
                                                    })}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">{generationFieldErrors('freeagentContactId')[0]}</Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group controlId="generation-project" className="mb-3 col-md-6">
                                                <Form.Label>Project{generationForm.contract.type === 'one_off' ? ' *' : ''}</Form.Label>
                                                <Form.Select
                                                    value={generationForm.freeagentProjectId}
                                                    onChange={event => {
                                                        setGenerationErrors({});
                                                        setGenerationForm(prev => ({ ...prev, freeagentProjectId: Number(event.target.value) || 0 }));
                                                    }}
                                                    disabled={projectsLoading}
                                                    isInvalid={generationFieldIsInvalid('freeagentProjectId')}
                                                    data-generation-field="freeagentProjectId"
                                                    aria-invalid={generationFieldIsInvalid('freeagentProjectId')}
                                                >
                                                    <option value={0}>{projectsLoading ? 'Loading projects...' : 'Select a Project'}</option>
                                                    {projects.map(project => <option key={project.id} value={project.id}>{project.name}</option>)}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">{generationFieldErrors('freeagentProjectId')[0]}</Form.Control.Feedback>
                                            </Form.Group>
                                        </div>

                                        <div className="row">
                                            <Form.Group controlId="generation-contract-name" className="mb-3 col-md-6">
                                                <Form.Label>Contract Name</Form.Label>
                                                <Form.Control
                                                    name="customerName"
                                                    value={generationForm.contract.customerName}
                                                    onChange={handleGenerationContractChange}
                                                    isInvalid={generationFieldIsInvalid('contract.customerName')}
                                                    data-generation-field="contract.customerName"
                                                    aria-invalid={generationFieldIsInvalid('contract.customerName')}
                                                />
                                                <Form.Control.Feedback type="invalid">{generationFieldErrors('contract.customerName')[0]}</Form.Control.Feedback>
                                            </Form.Group>
                                            {generationForm.contract.type === 'recurring' && (
                                                <Form.Group controlId="generation-recurring-invoice" className="mb-3 col-md-6">
                                                    <Form.Label>Recurring Invoice ID</Form.Label>
                                                    <Form.Control type="number" name="freeagentRecurringInvoiceId" value={generationForm.contract.freeagentRecurringInvoiceId} onChange={handleGenerationContractChange} />
                                                </Form.Group>
                                            )}
                                        </div>

                                        <div className="row">
                                            <Form.Group controlId="generation-start-date" className="mb-3 col-md-3">
                                                <Form.Label>Start Date</Form.Label>
                                                <DatePicker selected={generationForm.contract.startDate} onChange={date => date && setGenerationForm(prev => ({ ...prev, contract: { ...prev.contract, startDate: date } }))} className="form-control" />
                                            </Form.Group>
                                            <Form.Group controlId="generation-expiry-date" className="mb-3 col-md-3">
                                                <Form.Label>Expiry Date</Form.Label>
                                                <DatePicker selected={generationForm.contract.expiryDate} onChange={date => date && setGenerationForm(prev => ({ ...prev, contract: { ...prev.contract, expiryDate: date } }))} className="form-control" />
                                            </Form.Group>
                                            {generationForm.contract.type === 'recurring' && (
                                                <Form.Group controlId="generation-hours" className="mb-3 col-md-3">
                                                    <Form.Label>Hours Per Month</Form.Label>
                                                    <Form.Control type="number" step="0.01" name="hoursPerMonth" value={generationForm.contract.hoursPerMonth} onChange={handleGenerationContractChange} />
                                                </Form.Group>
                                            )}
                                            <Form.Group controlId="generation-value" className="mb-3 col-md-3">
                                                <Form.Label>Total Contract Value</Form.Label>
                                                <Form.Control type="number" step="0.01" name="totalContractValue" value={generationForm.contract.totalContractValue} onChange={handleGenerationContractChange} />
                                            </Form.Group>
                                        </div>

                                        <Form.Group controlId="generation-contract-notes" className="mb-3">
                                            <Form.Label>Contract Notes</Form.Label>
                                            <Form.Control as="textarea" rows={2} name="notes" value={generationForm.contract.notes} onChange={handleGenerationContractChange} />
                                        </Form.Group>
                                    </section>
                                )}

                                <div className="row">
                                    {selectedGenerationFields.map(field => {
                                        const errorKey = `payload.${field.name}`;
                                        const isInvalid = generationFieldIsInvalid(errorKey, field.name);
                                        const storedValue = generationForm.payload[field.name] ?? '';
                                        const dateInputValue = field.type === 'date' ? generationDateInputValue(storedValue) : '';
                                        const useLegacyDateTextInput = field.type === 'date' && Boolean(storedValue) && !dateInputValue;
                                        const sharedProps = {
                                            value: field.type === 'date' && !useLegacyDateTextInput ? dateInputValue : storedValue,
                                            onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => handleGenerationPayloadChange(field, event.target.value),
                                            isInvalid,
                                            'data-generation-field': errorKey,
                                            'aria-invalid': isInvalid,
                                        };
                                        return (
                                            <Form.Group controlId={`generation-payload-${field.name.toLowerCase()}`} className={`mb-3 ${fullWidthGenerationFields.has(field.name) ? 'col-md-12' : 'col-md-6'}`} key={field.name}>
                                                <Form.Label>{field.label}{field.required || Object.entries(field.requiredWhen ?? {}).some(([dependency, expected]) => generationForm.payload[dependency] === expected) ? ' *' : ''}</Form.Label>
                                                {field.type === 'textarea' ? (
                                                    <Form.Control as="textarea" rows={field.name === 'SITE_SPECIFICATION' ? 7 : 3} {...sharedProps} />
                                                ) : field.type === 'select' ? (
                                                    <Form.Select {...sharedProps}>
                                                        <option value="">Select...</option>
                                                        {(field.options ?? []).map(option => (
                                                            <option key={option.value} value={option.value}>{option.label}</option>
                                                        ))}
                                                    </Form.Select>
                                                ) : (
                                                    <Form.Control type={field.type === 'money' || useLegacyDateTextInput ? 'text' : field.type} {...sharedProps} />
                                                )}
                                                <Form.Control.Feedback type="invalid">{generationFieldErrors(errorKey, field.name)[0]}</Form.Control.Feedback>
                                            </Form.Group>
                                        );
                                    })}
                                </div>

                                <div className="row">
                                    <Form.Group controlId="generation-signer-name" className="mb-3 col-md-4">
                                        <Form.Label>Client Signer Name</Form.Label>
                                        <Form.Control
                                            value={effectiveSignerName}
                                            onChange={event => {
                                                const value = event.target.value;
                                                setGenerationErrors({});
                                                setGenerationForm(prev => ({ ...prev, signerName: value, payload: { ...prev.payload, CLIENT_SIGNER_NAME: value } }));
                                            }}
                                            isInvalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_NAME', 'CLIENT_SIGNER_NAME', 'signerName')}
                                            data-generation-field="payload.CLIENT_SIGNER_NAME"
                                            aria-invalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_NAME', 'CLIENT_SIGNER_NAME', 'signerName')}
                                        />
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('payload.CLIENT_SIGNER_NAME', 'CLIENT_SIGNER_NAME', 'signerName')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="generation-signer-email" className="mb-3 col-md-4">
                                        <Form.Label>Client Signer Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            value={effectiveSignerEmail}
                                            onChange={event => {
                                                const value = event.target.value;
                                                setGenerationErrors({});
                                                setGenerationForm(prev => ({ ...prev, signerEmail: value, payload: { ...prev.payload, CLIENT_SIGNER_EMAIL: value } }));
                                            }}
                                            isInvalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_EMAIL', 'CLIENT_SIGNER_EMAIL', 'signerEmail')}
                                            data-generation-field="payload.CLIENT_SIGNER_EMAIL"
                                            aria-invalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_EMAIL', 'CLIENT_SIGNER_EMAIL', 'signerEmail')}
                                        />
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('payload.CLIENT_SIGNER_EMAIL', 'CLIENT_SIGNER_EMAIL', 'signerEmail')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                    <Form.Group controlId="generation-signer-role" className="mb-3 col-md-4">
                                        <Form.Label>Client Signer Role</Form.Label>
                                        <Form.Control
                                            value={generationForm.payload.CLIENT_SIGNER_TITLE ?? ''}
                                            onChange={event => {
                                                const value = event.target.value;
                                                setGenerationErrors({});
                                                setGenerationForm(prev => ({ ...prev, payload: { ...prev.payload, CLIENT_SIGNER_TITLE: value } }));
                                            }}
                                            isInvalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_TITLE', 'CLIENT_SIGNER_TITLE')}
                                            data-generation-field="payload.CLIENT_SIGNER_TITLE"
                                            aria-invalid={generationFieldIsInvalid('payload.CLIENT_SIGNER_TITLE', 'CLIENT_SIGNER_TITLE')}
                                        />
                                        <Form.Control.Feedback type="invalid">{generationFieldErrors('payload.CLIENT_SIGNER_TITLE', 'CLIENT_SIGNER_TITLE')[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </div>
                                <Form.Group controlId="generation-email-subject" className="mb-3">
                                    <Form.Label>Email Subject</Form.Label>
                                    <Form.Control value={generationForm.emailSubject} onChange={event => setGenerationForm(prev => ({ ...prev, emailSubject: event.target.value }))} />
                                </Form.Group>
                            </Form>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseGenerationModal}>Close</Button>
                    <Button variant="primary" onClick={handleGenerateContract} disabled={loadingGenerationDetail || generationDetailLoadFailed || generatingContract || hasBlockingGenerationErrors}>
                        {generatingContract ? 'Generating...' : previewIsStale ? 'Regenerate Preview' : 'Generate Preview'}
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleSendGeneratedContract}
                        disabled={loadingGenerationDetail || generationDetailLoadFailed || hasBlockingGenerationErrors || sendingDocusign || !generatedContract || !effectiveSignerEmail || !effectiveSignerName || previewIsStale}
                    >
                        {sendingDocusign ? 'Sending...' : 'Send with DocuSign'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ContractManager;
